/**
 * ============================================================================
 * MINIMUM ORDER MANAGER
 * ============================================================================
 *
 * Manages minimum order values based on geolocation and provides:
 * - Progress bar visualization in cart drawer
 * - Checkout blocking when minimum not reached
 * - Optional free gift system
 * - Cart page redirect to homepage + drawer
 *
 * @version 1.0.0
 * @author DeSight Studio GmbH
 *
 * ============================================================================
 */

class MinimumOrderManager {
  constructor() {
    this.eventBus = window.DeSightApp?.getEventBus();

    // Default minimum order values (in cents)
    this.minimumOrderValues = {
      'AT': 2000, // 20€ Austria
      'DE': 3000, // 30€ Germany
      'EU': 4900  // 49€ EU
    };

    // Current state
    this.currentCountry = null;
    this.currentMinimum = 0;
    this.cartTotal = 0;
    this.cartTotalWithoutDiscount = 0;
    this.freeGiftProduct = null;
    this.freeGiftDiscountCode = null;
    this.freeGiftMethod = 'disabled';
    this.freeGiftInCart = false;
    this.freeGiftQuantityInCart = 0;
    this.freeGiftEnabled = false;
    this.giftApplied = false;

    // Processing flags to prevent race conditions
    this.isProcessingUpdate = false;
    this.isApplyingGift = false;
    this.isRemovingGift = false;
    this.isDetectingCountry = false;
    this.manualRemovalInProgress = false; // CRITICAL FIX: Flag to prevent automatic gift removal during manual removal

    // Cooldown to prevent immediate re-application after user removes gift
    this.giftRemovalCooldown = false;
    this.lastGiftRemovalTime = 0;

    // DOM elements
    this.progressContainer = null;
    this.checkoutButton = null;

    // Debounce timers
    this.cartUpdateTimeout = null;
    this.uiUpdateTimeout = null;

    // Store last cart data for gift detection
    this.lastCartData = null;

    // Wait for theme settings to be available
    if (window.theme?.settings) {
      this.init();
    } else {
      // Wait for settings to load
      const checkSettings = () => {
        if (window.theme?.settings) {
          this.init();
        } else {
          setTimeout(checkSettings, 100);
        }
      };
      setTimeout(checkSettings, 100);
    }
  }

  /**
   * Initialize the minimum order manager
   */
  init() {
    console.log('🛒 Minimum Order Manager initializing...');

    this.loadSettings();

    // Exit early if system is disabled
    if (!this.enabled) {
      console.log('🛒 Minimum Order System disabled, skipping initialization');
      return;
    }

    this.detectCountry();
    this.bindEvents();
    this.loadCartData(); // Load initial cart data
    this.initProgressBar();
    this.handleCartPageRedirect();

    // Force disable gift controls after initialization (only if cart has items)
    setTimeout(async () => {
      await this.loadCartData();
      if (this.lastCartData?.items?.length > 0) {
        this.forceDisableGiftControls();
      }
    }, 1000);

    console.log('✅ Minimum Order Manager initialized');
    if (this.debugMode) {
      console.log('🛒 Current state:', {
        enabled: this.enabled,
        country: this.currentCountry,
        minimum: this.currentMinimum,
        cartTotal: this.cartTotal,
        progressContainer: !!this.progressContainer,
        freeGiftEnabled: this.freeGiftEnabled
      });
    }
  }

  /**
   * Load settings from theme settings
   */
  loadSettings() {
    // Load from Shopify theme settings
    if (window.theme?.settings) {
      const settings = window.theme.settings;

      // Check if system is enabled
      this.enabled = settings.minimum_order_enabled !== false;
      this.debugMode = settings.minimum_order_debug_mode === true || window.location.search.includes('debug=true');
      this.fallbackCountry = settings.minimum_order_fallback_country || 'EU';

      if (!this.enabled) {
        console.log('🛒 Minimum Order System is disabled in theme settings');
        return;
      }

      this.minimumOrderValues = {
        'AT': (settings.minimum_order_austria || 20) * 100,
        'DE': (settings.minimum_order_germany || 30) * 100,
        'EU': (settings.minimum_order_eu || 50) * 100
      };

      // Free gift settings
      this.freeGiftMethod = settings.free_gift_method || 'disabled';
      this.freeGiftEnabled = this.freeGiftMethod !== 'disabled';

      if (this.freeGiftEnabled) {
        // Load product configuration if available
        if (settings.free_gift_product) {
          const variantId = settings.free_gift_variant_id || settings.free_gift_product;

          this.freeGiftProduct = {
            id: settings.free_gift_product,
            variantId: variantId
          };

          // Load additional product information for better recognition
          this.loadGiftProductInfo();

          if (this.debugMode) {
            console.log('🎁 Free gift product configured:', {
              productId: settings.free_gift_product,
              variantId: variantId,
              customVariantId: settings.free_gift_variant_id
            });
          }
        }

        // Load discount configuration if available
        if (settings.free_gift_discount_code) {
          this.freeGiftDiscountCode = settings.free_gift_discount_code;

          if (this.debugMode) {
            console.log('🎁 Free gift discount configured:', {
              discountCode: this.freeGiftDiscountCode
            });
          }
        }
      }

      // Multilingual texts
      this.currentLanguage = window.theme?.currentLanguage || 'de';
      this.allTexts = window.theme?.settings?.texts || {};

      // Fallback texts if theme settings are not available
      this.fallbackTexts = {
        de: {
          remaining: 'Noch {amount} bis zum Mindestbestellwert',
          reached: 'Mindestbestellwert erreicht!',
          giftAvailable: 'Kostenloses Geschenk hinzugefügt!',
          giftWhenMinimum: 'Bei Erreichen des Mindestbestellwerts: Kostenloses Geschenk',
          checkoutBlocked: 'Mindestbestellwert noch nicht erreicht. Noch {amount} hinzufügen.'
        },
        en: {
          remaining: 'Only {amount} more to reach minimum order',
          reached: 'Minimum order reached!',
          giftAvailable: 'Free gift added!',
          giftWhenMinimum: 'When minimum order is reached: Free gift',
          checkoutBlocked: 'Minimum order not reached yet. Add {amount} more.'
        },
        it: {
          remaining: 'Solo {amount} per raggiungere l\'ordine minimo',
          reached: 'Ordine minimo raggiunto!',
          giftAvailable: 'Regalo gratuito aggiunto!',
          giftWhenMinimum: 'Al raggiungimento dell\'ordine minimo: Regalo gratuito',
          checkoutBlocked: 'Ordine minimo non ancora raggiunto. Aggiungi {amount}.'
        }
      };

      if (this.debugMode) {
        console.log('🛒 Settings loaded:', {
          enabled: this.enabled,
          minimumValues: this.minimumOrderValues,
          freeGiftEnabled: this.freeGiftEnabled,
          freeGiftMethod: this.freeGiftMethod,
          freeGiftProduct: this.freeGiftProduct,
          freeGiftDiscountCode: this.freeGiftDiscountCode,
          fallbackCountry: this.fallbackCountry
        });
      }
    }
  }

  /**
   * Load additional gift product information for better recognition
   */
  async loadGiftProductInfo() {
    if (!this.freeGiftProduct?.id) return;

    try {
      // Try to get product info from Shopify's product API
      const response = await fetch(`/products/${this.freeGiftProduct.id}.js`);
      if (response.ok) {
        const productData = await response.json();

        this.freeGiftProduct.handle = productData.handle;
        this.freeGiftProduct.title = productData.title;

        if (this.debugMode) {
          console.log('🎁 Gift product info loaded:', {
            id: this.freeGiftProduct.id,
            handle: this.freeGiftProduct.handle,
            title: this.freeGiftProduct.title,
            variantId: this.freeGiftProduct.variantId
          });
        }
      } else {
        // Fallback: Try to extract from current cart data
        await this.loadCartData();
        if (this.lastCartData?.items) {
          const giftItem = this.lastCartData.items.find(item =>
            item.product_id == this.freeGiftProduct.id
          );

          if (giftItem) {
            this.freeGiftProduct.handle = giftItem.handle;
            this.freeGiftProduct.title = giftItem.product_title || giftItem.title;

            if (this.debugMode) {
              console.log('🎁 Gift product info from cart:', {
                id: this.freeGiftProduct.id,
                handle: this.freeGiftProduct.handle,
                title: this.freeGiftProduct.title
              });
            }
          }
        }
      }
    } catch (error) {
      if (this.debugMode) {
        console.log('🎁 Could not load gift product info:', error);
      }
    }
  }

  /**
   * Detect user's country via reliable external API
   */
  async detectCountry() {
    // Prevent multiple simultaneous country detections
    if (this.isDetectingCountry) {
      if (this.debugMode) {
        console.log('🌍 Country detection already in progress, skipping...');
      }
      return;
    }

    this.isDetectingCountry = true;

    try {
      if (this.debugMode) {
        console.log('🌍 Starting country detection...');
      }

      // Method 1: Try Shopify's geolocation if available (but don't trust it completely)
      if (window.Shopify?.country) {
        console.log('🌍 Shopify country detected:', window.Shopify.country, '(but will verify with API)');
        // Don't return here - continue to API verification
      }

      // Method 2: Use reliable external API
      const countryFromAPI = await this.getCountryFromAPI();

      if (countryFromAPI) {
        console.log('🌍 API detection success:', countryFromAPI);
        this.setCountry(countryFromAPI);
        return;
      }

      // Method 3: Enhanced timezone-based detection as fallback
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const countryFromTimezone = this.getCountryFromTimezone(timezone);

      if (this.debugMode) {
        console.log('🌍 Using timezone fallback:', {
          timezone: timezone,
          detectedCountry: countryFromTimezone
        });
      }

      this.setCountry(countryFromTimezone);

    } catch (error) {
      console.warn('🌍 All country detection failed, using fallback:', this.fallbackCountry, error);
      this.setCountry(this.fallbackCountry);
    } finally {
      this.isDetectingCountry = false;
    }
  }

  /**
   * Enhanced timezone-based country detection
   */
  getCountryFromTimezone(timezone) {
    const timezoneMap = {
      // Austria
      'Europe/Vienna': 'AT',
      'Europe/Salzburg': 'AT',
      'Europe/Graz': 'AT',
      'Europe/Linz': 'AT',
      'Europe/Innsbruck': 'AT',

      // Germany
      'Europe/Berlin': 'DE',
      'Europe/Munich': 'DE',
      'Europe/Hamburg': 'DE',
      'Europe/Cologne': 'DE',
      'Europe/Frankfurt': 'DE',
      'Europe/Stuttgart': 'DE',
      'Europe/Dortmund': 'DE',
      'Europe/Essen': 'DE',
      'Europe/Dresden': 'DE',
      'Europe/Leipzig': 'DE',
      'Europe/Bremen': 'DE',
      'Europe/Hanover': 'DE',
      'Europe/Nuremberg': 'DE',
      'Europe/Duisburg': 'DE',

      // Switzerland (treat as EU for pricing)
      'Europe/Zurich': 'EU',
      'Europe/Geneva': 'EU',
      'Europe/Basel': 'EU',

      // Other EU countries
      'Europe/Rome': 'EU',
      'Europe/Milan': 'EU',
      'Europe/Paris': 'EU',
      'Europe/Madrid': 'EU',
      'Europe/Barcelona': 'EU',
      'Europe/Amsterdam': 'EU',
      'Europe/Brussels': 'EU',
      'Europe/Prague': 'EU',
      'Europe/Warsaw': 'EU',
      'Europe/Budapest': 'EU',
      'Europe/Stockholm': 'EU',
      'Europe/Copenhagen': 'EU',
      'Europe/Oslo': 'EU',
      'Europe/Helsinki': 'EU',
      'Europe/Dublin': 'EU',
      'Europe/London': 'EU',
      'Europe/Lisbon': 'EU',
      'Europe/Athens': 'EU'
    };

    // Direct timezone match
    if (timezoneMap[timezone]) {
      return timezoneMap[timezone];
    }

    // Fallback: check if timezone contains country indicators
    if (timezone.includes('Vienna') || timezone.includes('Austria')) {
      return 'AT';
    }
    if (timezone.includes('Berlin') || timezone.includes('Germany')) {
      return 'DE';
    }
    if (timezone.includes('Europe/')) {
      return 'EU';
    }

    // Default fallback
    return this.fallbackCountry || 'EU';
  }

  /**
   * Get country from reliable external API
   */
  async getCountryFromAPI() {
    // Use ipapi.com - most reliable and fast
    try {
      if (this.debugMode) {
        console.log('🌍 Trying ipapi.com...');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch('https://ipapi.com/ip_api.php?ip=', {
        signal: controller.signal,
        mode: 'cors',
        headers: {
          'Accept': 'application/json'
        }
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();

        if (data.country_code && data.country_code.length === 2) {
          if (this.debugMode) {
            console.log('🌍 ipapi.com success:', {
              country: data.country_code,
              country_name: data.country_name,
              city: data.city
            });
          }
          return data.country_code.toUpperCase();
        }
      }
    } catch (error) {
      if (this.debugMode) {
        console.log('🌍 ipapi.com failed:', error.message);
      }
    }

    // Fallback to ip-api.com
    try {
      if (this.debugMode) {
        console.log('🌍 Trying ip-api.com as fallback...');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch('https://ip-api.com/json/', {
        signal: controller.signal,
        mode: 'cors',
        headers: {
          'Accept': 'application/json'
        }
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();

        if (data.countryCode && data.countryCode.length === 2) {
          if (this.debugMode) {
            console.log('🌍 ip-api.com success:', {
              country: data.countryCode,
              country_name: data.country,
              city: data.city
            });
          }
          return data.countryCode.toUpperCase();
        }
      }
    } catch (error) {
      if (this.debugMode) {
        console.log('🌍 ip-api.com failed:', error.message);
      }
    }

    return null;
  }

  /**
   * Detect country from browser language as final fallback
   */
  getCountryFromBrowserLanguage() {
    const language = navigator.language || navigator.userLanguage;

    if (this.debugMode) {
      console.log('🌍 Browser language:', language);
    }

    if (language) {
      const langCode = language.toLowerCase();

      // Common language-country mappings
      if (langCode.includes('de-at') || langCode.includes('de_at')) {
        return 'AT';
      }
      if (langCode.includes('de-de') || langCode.includes('de_de') || langCode.startsWith('de')) {
        return 'DE';
      }
      if (langCode.includes('en-gb') || langCode.includes('en_gb')) {
        return 'EU'; // UK treated as EU for pricing
      }
      if (langCode.includes('it-') || langCode.startsWith('it')) {
        return 'EU';
      }
      if (langCode.includes('fr-') || langCode.startsWith('fr')) {
        return 'EU';
      }
      if (langCode.includes('es-') || langCode.startsWith('es')) {
        return 'EU';
      }
    }

    return this.fallbackCountry || 'EU';
  }

  /**
   * Set the current country and minimum order value
   */
  setCountry(countryCode) {
    const originalCountry = countryCode;
    const newCountry = countryCode.toUpperCase();

    // Prevent unnecessary updates if country hasn't changed
    if (this.currentCountry === newCountry) {
      if (this.debugMode) {
        console.log(`🌍 Country already set to ${newCountry}, skipping update`);
      }
      return;
    }

    this.currentCountry = newCountry;

    if (this.debugMode) {
      console.log(`🌍 Setting country: ${originalCountry} -> ${this.currentCountry}`);
      console.log('🌍 Available minimum values:', this.minimumOrderValues);
    }

    // Simple logic: AT=20€, DE=30€, everything else=49€
    if (this.currentCountry === 'AT') {
      this.currentMinimum = this.minimumOrderValues['AT'];
      console.log(`🌍 Austria detected: ${this.currentMinimum / 100}€`);
    } else if (this.currentCountry === 'DE') {
      this.currentMinimum = this.minimumOrderValues['DE'];
      console.log(`🌍 Germany detected: ${this.currentMinimum / 100}€`);
    } else {
      this.currentMinimum = this.minimumOrderValues['EU'];
      console.log(`🌍 Other country (${this.currentCountry}), using EU default: ${this.currentMinimum / 100}€`);
    }

    console.log(`🌍 Final result: Country=${this.currentCountry}, Minimum=${this.currentMinimum / 100}€`);

    // Only update UI if not currently processing to prevent loops
    if (!this.isProcessingUpdate) {
      this.scheduleUIUpdate();
    }
  }

  /**
   * Bind event listeners - SIMPLIFIED to prevent race conditions
   */
  bindEvents() {
    // Only listen to essential cart events to prevent loops
    const handleCartEvent = (eventName, detail = null) => {
      if (this.debugMode) {
        console.log('🛒 Cart event detected:', eventName, detail, {
          manualRemovalInProgress: this.manualRemovalInProgress
        });
      }

      // CRITICAL FIX: COMPLETELY SKIP all cart updates if manual removal is in progress
      if (this.manualRemovalInProgress) {
        if (this.debugMode) {
          console.log('🛒 SKIPPING cart update - MinimumOrderManager disabled during manual operation');
        }
        return; // Do nothing - let the normal cart system handle everything
      }

      // Only process cart updates when no manual operations are in progress
      this.handleCartUpdate();
    };

    // Listen only to the most reliable cart events
    document.addEventListener('cart:update', (e) => handleCartEvent('cart:update', e.detail));
    document.addEventListener('cart:updated', (e) => handleCartEvent('cart:updated', e.detail));
    document.addEventListener('cart:change', (e) => handleCartEvent('cart:change', e.detail));

    // ADDITIONAL: Listen for CartAddEvent specifically (for Schmankerl and other components)
    document.addEventListener('cart:update', (e) => {
      if (e.detail?.data?.source) {
        if (this.debugMode) {
          console.log('🛒 CartAddEvent detected from source:', e.detail.data.source);
        }
        // Handle all cart add events regardless of source
        handleCartEvent('cart:add-' + e.detail.data.source, e.detail);
      }
    });

    // Listen for quantity changes directly on input elements
    document.addEventListener('input', (event) => {
      if (event.target.matches('input[name*="updates"]') ||
          event.target.matches('.cart-item__quantity-input') ||
          event.target.matches('[data-quantity-input]') ||
          event.target.matches('.quantity__input')) {
        if (this.debugMode) {
          console.log('🛒 Quantity input changed, scheduling cart check');
        }
        // Immediate check for quantity changes
        setTimeout(() => this.handleCartUpdate(), 500);
      }
    });

    // Listen for cart drawer open to ensure UI is updated
    document.addEventListener('cart-drawer:open', () => {
      if (this.debugMode) {
        console.log('🛒 Cart drawer opened');
      }
      // Delayed UI update when drawer opens
      setTimeout(async () => {
        // SAFETY: Load fresh cart data first
        await this.loadCartData();

        // Ensure cart drawer functionality (especially for empty cart)
        this.ensureCartDrawerFunctionality();

        this.ensureProgressBarExists();
        this.updateProgressBar();
        this.updateCheckoutButton();
        this.applyGiftStyling(); // Apply gift styling when drawer opens
        this.updateGiftProductControls(); // Update gift controls when drawer opens

        // Force disable gift controls as backup (only if cart has items)
        if (this.lastCartData?.items?.length > 0) {
          setTimeout(() => {
            this.forceDisableGiftControls();
          }, 100);
        }
      }, 200);
    });

    // Listen for remove button clicks with anti-abuse check
    document.addEventListener('click', (event) => {
      if (event.target.matches('[data-cart-remove]') ||
          event.target.matches('.cart-remove') ||
          event.target.matches('.cart__remove') ||
          event.target.closest('[data-cart-remove]') ||
          event.target.closest('.cart-remove') ||
          event.target.closest('.cart__remove')) {

        if (this.debugMode) {
          console.log('🛒 Remove button clicked - COMPLETELY DISABLING MinimumOrderManager');
        }

        // CRITICAL FIX: COMPLETELY disable MinimumOrderManager during manual removal
        this.manualRemovalInProgress = true;

        // Check if removing free gift product
        this.checkForFreeGiftRemoval(event);

        // Check if removing regular product (not gift) - clear cooldown
        this.checkForRegularProductRemoval(event);

        // CRITICAL: Do NOT call handleCartUpdate() immediately - let the normal cart system handle it
        // Only re-enable after a longer delay to ensure all cart operations are complete
        setTimeout(() => {
          this.manualRemovalInProgress = false;
          if (this.debugMode) {
            console.log('🛒 Manual removal flag cleared - MinimumOrderManager re-enabled');
          }
          // Only now check if we need to update anything
          setTimeout(() => {
            this.handleCartUpdate();
          }, 200);
        }, 3000); // Much longer delay to avoid interference
      }

      // SPECIAL: Listen for Schmankerl add-to-cart button clicks
      const schmankerButton = event.target.closest('.schmankerl-des-monats__product-card__cta');
      if (schmankerButton) {
        if (this.debugMode) {
          console.log('🛒 Schmankerl add-to-cart button clicked, scheduling cart check');
        }
        // Schedule cart update check after form submission
        setTimeout(() => this.handleCartUpdate(), 1000);
      }
    });

    // SPECIAL: Listen for any form submissions that might add to cart
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form && form.matches('form[data-type="add-to-cart-form"]')) {
        if (this.debugMode) {
          console.log('🛒 Add-to-cart form submitted, scheduling cart check');
        }
        // Schedule cart update check after form submission
        setTimeout(() => this.handleCartUpdate(), 1000);
      }
    });

    // Listen for quantity selector button clicks (+ and - buttons) - CAPTURE PHASE
    document.addEventListener('click', (event) => {
      if (event.target.matches('.quantity-minus, .quantity-plus') ||
          event.target.closest('.quantity-minus, .quantity-plus')) {
        if (this.handleQuantityButtonClick(event)) {
          return; // Event was handled and prevented
        }
      }
    }, true); // Use capture phase to intercept early

    // Listen for direct quantity input changes - CAPTURE PHASE
    document.addEventListener('input', (event) => {
      if (event.target.matches('input[name="updates[]"]') ||
          event.target.matches('input[data-cart-line]')) {
        if (this.handleQuantityInputChange(event)) {
          return; // Event was handled and prevented
        }
      }
    }, true); // Use capture phase

    // Listen for QuantitySelectorUpdateEvent - CAPTURE PHASE
    document.addEventListener('QuantitySelectorUpdateEvent', (event) => {
      if (this.handleQuantitySelectorEvent(event)) {
        return; // Event was handled and prevented
      }

      // CRITICAL FIX: COMPLETELY disable MinimumOrderManager during quantity changes
      this.manualRemovalInProgress = true;
      if (this.debugMode) {
        console.log('🛒 Quantity change detected - COMPLETELY DISABLING MinimumOrderManager');
      }

      // Clear the flag after a longer delay to allow all cart processing to complete
      setTimeout(() => {
        this.manualRemovalInProgress = false;
        if (this.debugMode) {
          console.log('🛒 Manual removal flag cleared after quantity change - MinimumOrderManager re-enabled');
        }
        // Only now check if we need to update anything
        setTimeout(() => {
          this.handleCartUpdate();
        }, 200);
      }, 4000); // Even longer delay for quantity changes as they can be more complex
    }, true); // Use capture phase

    // ADDITIONAL: Listen for any custom cart events that might be missed
    const additionalCartEvents = ['cart:add', 'cart:item-added', 'shopify:cart:update', 'theme:cart:updated'];
    additionalCartEvents.forEach(eventName => {
      document.addEventListener(eventName, (e) => {
        if (this.debugMode) {
          console.log('🛒 Additional cart event detected:', eventName, e.detail);
        }
        handleCartEvent(eventName, e.detail);
      });
    });

    if (this.debugMode) {
      console.log('🛒 Simplified event listeners bound successfully');
    }
  }

  /**
   * Check if free gift product is being removed and handle anti-abuse
   */
  checkForFreeGiftRemoval(event) {
    if (!this.freeGiftProduct || !this.freeGiftDiscountCode) {
      return;
    }

    // Try to find the product being removed
    const removeButton = event.target.closest('[data-cart-remove]') ||
                        event.target.closest('.cart-remove') ||
                        event.target.closest('.cart__remove');

    if (!removeButton) return;

    // Check if this is our free gift product
    const cartItem = removeButton.closest('.cart-item') ||
                    removeButton.closest('[data-cart-item]') ||
                    removeButton.closest('tr');

    if (!cartItem) return;

    // Look for product ID or variant ID in the cart item
    const productId = cartItem.dataset.productId ||
                     cartItem.dataset.variantId ||
                     cartItem.querySelector('[data-product-id]')?.dataset.productId ||
                     cartItem.querySelector('[data-variant-id]')?.dataset.variantId;

    if (productId && (productId == this.freeGiftProduct.id || productId == this.freeGiftProduct.variant_id)) {
      if (this.debugMode) {
        console.log('🚨 Free gift product being removed by user, activating cooldown');
      }

      // Activate cooldown to prevent immediate re-application
      this.giftRemovalCooldown = true;
      this.lastGiftRemovalTime = Date.now();

      // Schedule discount removal after cart update
      setTimeout(async () => {
        await this.removeDiscountCode();
        this.giftApplied = false;
        this.freeGiftInCart = false;
      }, 1000);

      // Clear cooldown after 10 seconds
      setTimeout(() => {
        this.giftRemovalCooldown = false;
        if (this.debugMode) {
          console.log('🚨 Gift removal cooldown cleared');
        }
      }, 10000);
    }
  }

  /**
   * Check if regular product (not gift) is being removed and clear cooldown
   */
  checkForRegularProductRemoval(event) {
    if (!this.freeGiftProduct || !this.giftRemovalCooldown) {
      return;
    }

    // Try to find the product being removed
    const removeButton = event.target.closest('[data-cart-remove]') ||
                        event.target.closest('.cart-remove') ||
                        event.target.closest('.cart__remove');

    if (!removeButton) return;

    // Check if this is our free gift product
    const cartItem = removeButton.closest('.cart-item') ||
                    removeButton.closest('[data-cart-item]') ||
                    removeButton.closest('tr');

    if (!cartItem) return;

    // Look for product ID or variant ID in the cart item
    const productId = cartItem.dataset.productId ||
                     cartItem.dataset.variantId ||
                     cartItem.querySelector('[data-product-id]')?.dataset.productId ||
                     cartItem.querySelector('[data-variant-id]')?.dataset.variantId;

    // If this is NOT our gift product, clear the cooldown
    if (productId && !(productId == this.freeGiftProduct.id || productId == this.freeGiftProduct.variant_id)) {
      if (this.debugMode) {
        console.log('🛒 Regular product being removed, clearing gift cooldown to allow proper cart management');
      }

      this.giftRemovalCooldown = false;
    }
  }

  /**
   * Apply styling to gift products in cart
   */
  applyGiftStyling() {
    if (!this.freeGiftProduct || !this.lastCartData) {
      if (this.debugMode) {
        console.log('🎁 Gift styling skipped - no product or cart data:', {
          hasProduct: !!this.freeGiftProduct,
          hasCartData: !!this.lastCartData
        });
      }
      return;
    }

    // Only apply styling if gift is actually in cart
    if (!this.freeGiftInCart) {
      if (this.debugMode) {
        console.log('🎁 Gift styling skipped - gift not in cart yet');
      }
      return;
    }

    if (this.debugMode) {
      console.log('🎁 Applying gift styling...', {
        giftProductId: this.freeGiftProduct.id,
        cartItems: this.lastCartData.items.length,
        freeGiftInCart: this.freeGiftInCart
      });
    }

    // Find all cart items
    const cartItems = document.querySelectorAll('.cart-item-tile, .cart-items__table-row');

    if (this.debugMode) {
      console.log('🎁 Found cart items in DOM:', cartItems.length);
    }

    cartItems.forEach((cartItem, index) => {
      // Get the line index from the cart item
      const lineIndex = cartItem.dataset.itemIndex || index;
      const cartItemData = this.lastCartData.items[parseInt(lineIndex)];

      if (!cartItemData) {
        if (this.debugMode) {
          console.log('🎁 No cart data for item index:', lineIndex);
        }
        return;
      }

      if (this.debugMode) {
        console.log('🎁 Checking cart item:', {
          index: lineIndex,
          productId: cartItemData.product_id,
          variantId: cartItemData.variant_id,
          properties: cartItemData.properties,
          giftProductId: this.freeGiftProduct.id
        });
      }

      // Check if this is our gift product
      if (cartItemData.product_id == this.freeGiftProduct.id) {
        // Check if this is a free gift (has the property)
        const isFreeGift = this.checkIfItemIsFreeGift(cartItemData);

        if (this.debugMode) {
          console.log('🎁 Gift product found, is free gift:', isFreeGift, {
            productId: cartItemData.product_id,
            properties: cartItemData.properties,
            note: 'Manual purchases of gift product should NOT be treated as gifts'
          });
        }

        if (isFreeGift) {
          // This is an actual free gift - apply gift styling
          this.addFreeGiftBadge(cartItem);
          this.hideRemoveButtonForFreeGift(cartItem);
        } else {
          // This is the same product but manually purchased - treat as normal product
          this.ensureRemoveButtonVisible(cartItem);
          this.removeFreeGiftBadge(cartItem);
          // Make sure quantity controls are enabled for manual purchases
          this.enableQuantityControlsForManualProduct(cartItem);
        }
      }
    });
  }

  /**
   * Check if cart item is a free gift based on properties
   */
  checkIfItemIsFreeGift(cartItemData) {
    if (!cartItemData) return false;

    // Check if item has free gift properties
    const properties = cartItemData.properties || {};
    const isFreeGift = properties._free_gift === 'true' || properties._minimum_order_gift === 'true';

    if (this.debugMode) {
      console.log('🎁 Checking if item is free gift:', {
        productId: cartItemData.product_id,
        properties: properties,
        isFreeGift: isFreeGift
      });
    }

    return isFreeGift;
  }

  /**
   * Add "Kostenlos" badge to gift product
   */
  addFreeGiftBadge(cartItem) {
    // Check if badge already exists
    if (cartItem.querySelector('.free-gift-badge')) return;

    // Find the image container
    const imageContainer = cartItem.querySelector('.cart-items__media-container');
    if (!imageContainer) return;

    // Create badge
    const badge = document.createElement('div');
    badge.className = 'free-gift-badge';

    // Get current language for badge text
    const currentLang = document.documentElement.lang || 'de';
    const badgeTexts = {
      'de': 'Kostenlos',
      'en': 'Free',
      'it': 'Gratuito'
    };

    badge.textContent = badgeTexts[currentLang] || badgeTexts['de'];

    // Add badge styles
    badge.style.cssText = `
      position: absolute;
      top: 8px;
      right: 8px;
      background: #22c55e;
      color: white;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      z-index: 10;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    `;

    // Make sure image container is positioned relative
    imageContainer.style.position = 'relative';

    // Add badge to image container
    imageContainer.appendChild(badge);

    if (this.debugMode) {
      console.log('🎁 Added free gift badge to product');
    }
  }

  /**
   * Remove "Kostenlos" badge from product
   */
  removeFreeGiftBadge(cartItem) {
    const existingBadge = cartItem.querySelector('.free-gift-badge');
    if (existingBadge) {
      existingBadge.remove();
      if (this.debugMode) {
        console.log('🎁 Removed free gift badge from manually added product');
      }
    }
  }

  /**
   * Enable quantity controls for manually purchased products (even if same as gift product)
   */
  enableQuantityControlsForManualProduct(cartItem) {
    const quantityButtons = cartItem.querySelectorAll('.quantity-minus, .quantity-plus, [data-quantity-minus], [data-quantity-plus]');
    const quantityInput = cartItem.querySelector('input[data-cart-line], input[name="updates[]"], .quantity__input');

    // Enable quantity buttons
    quantityButtons.forEach(button => {
      button.disabled = false;
      button.style.opacity = '1';
      button.style.pointerEvents = 'auto';
      button.style.cursor = 'pointer';
      button.title = '';
      button.removeAttribute('data-gift-disabled');
      button.classList.remove('gift-disabled');
    });

    // Enable quantity input
    if (quantityInput) {
      quantityInput.disabled = false;
      quantityInput.readOnly = false;
      quantityInput.style.opacity = '1';
      quantityInput.style.cursor = 'text';
      quantityInput.title = '';
      quantityInput.removeAttribute('data-gift-disabled');
      quantityInput.classList.remove('gift-disabled');
    }

    if (this.debugMode) {
      console.log('✅ Enabled quantity controls for manually purchased product');
    }
  }

  /**
   * Hide remove button for free gift
   */
  hideRemoveButtonForFreeGift(cartItem) {
    const removeButton = cartItem.querySelector('.cart-items__remove');
    if (removeButton) {
      removeButton.style.display = 'none';
      removeButton.setAttribute('data-free-gift-hidden', 'true');

      if (this.debugMode) {
        console.log('🎁 Hidden remove button for free gift');
      }
    }
  }

  /**
   * Ensure remove button is visible for manually added products
   */
  ensureRemoveButtonVisible(cartItem) {
    const removeButton = cartItem.querySelector('.cart-items__remove');
    if (removeButton && removeButton.getAttribute('data-free-gift-hidden')) {
      removeButton.style.display = '';
      removeButton.removeAttribute('data-free-gift-hidden');

      if (this.debugMode) {
        console.log('🎁 Ensured remove button visible for manually added product');
      }
    }
  }

  /**
   * Handle quantity button clicks (+ and - buttons)
   */
  handleQuantityButtonClick(event) {
    const button = event.target.closest('.quantity-minus, .quantity-plus');
    if (!button) return false;

    const cartItem = button.closest('.cart-item-tile, .cart-item, tr');
    if (!cartItem) return false;

    // Check if this is a gift product
    if (this.isGiftProduct(cartItem)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (this.debugMode) {
        console.log('🚨 Prevented quantity change on gift product via button click');
      }

      // Show user feedback
      this.showGiftQuantityWarning();
      return true; // Event was prevented
    }

    return false; // Event was not prevented
  }

  /**
   * Handle direct quantity input changes
   */
  handleQuantityInputChange(event) {
    const input = event.target;
    const cartItem = input.closest('.cart-item-tile, .cart-item, tr');
    if (!cartItem) return false;

    // Check if this is a gift product
    if (this.isGiftProduct(cartItem)) {
      const originalQuantity = input.dataset.originalQuantity || '1';

      if (this.debugMode) {
        console.log('🚨 Prevented quantity change on gift product via input change');
        console.log('🚨 Restoring original quantity:', originalQuantity);
      }

      // Restore original quantity
      input.value = originalQuantity;

      // Show user feedback
      this.showGiftQuantityWarning();

      // Prevent the change from being processed
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return true; // Event was prevented
    }

    return false; // Event was not prevented
  }

  /**
   * Handle QuantitySelectorUpdateEvent
   */
  handleQuantitySelectorEvent(event) {
    const { quantity, cartLine } = event.detail;

    // Find the cart item by line number
    const cartItems = document.querySelectorAll('.cart-item-tile, .cart-item, tr');
    const cartItem = Array.from(cartItems).find(item => {
      const input = item.querySelector('input[data-cart-line]');
      return input && parseInt(input.dataset.cartLine) === cartLine;
    });

    if (!cartItem) return false;

    // Check if this is a gift product
    if (this.isGiftProduct(cartItem)) {
      if (this.debugMode) {
        console.log('🚨 Prevented quantity change on gift product via QuantitySelectorUpdateEvent');
      }

      // Prevent the event from propagating
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Restore original quantity in the input
      const input = cartItem.querySelector('input[data-cart-line]');
      if (input) {
        const originalQuantity = input.dataset.originalQuantity || '1';
        input.value = originalQuantity;
      }

      // Show user feedback
      this.showGiftQuantityWarning();
      return true; // Event was prevented
    }

    return false; // Event was not prevented
  }

  /**
   * Check if cart item is a gift product (only actual free gifts, not manual purchases)
   */
  isGiftProduct(cartItem) {
    if (!this.freeGiftProduct?.handle) return false;

    // First check if this is the gift product by ID/handle
    let isGiftProductId = false;

    // Check by product link
    const productLink = cartItem.querySelector('a[href*="/products/"]');
    if (productLink && productLink.href.includes(this.freeGiftProduct.handle)) {
      isGiftProductId = true;
    }

    // Check by data attributes
    const productId = cartItem.dataset.productId;
    if (productId && productId == this.freeGiftProduct.id) {
      isGiftProductId = true;
    }

    // If this is not the gift product at all, return false
    if (!isGiftProductId) return false;

    // If it is the gift product, check if it's actually a free gift (has properties)
    // This prevents manual purchases from being treated as gifts
    return this.checkIfItemHasGiftProperties(cartItem);
  }

  /**
   * Show warning when user tries to change gift quantity
   */
  showGiftQuantityWarning() {
    // Create or show a temporary warning message
    let warning = document.querySelector('.gift-quantity-warning');

    if (!warning) {
      warning = document.createElement('div');
      warning.className = 'gift-quantity-warning';
      warning.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #d90700;
        color: white;
        padding: 12px 16px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: bold;
        z-index: 10000;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        transform: translateX(100%);
        transition: transform 0.3s ease;
      `;
      warning.textContent = 'Die Menge von Geschenkprodukten kann nicht geändert werden';
      document.body.appendChild(warning);
    }

    // Show the warning
    setTimeout(() => {
      warning.style.transform = 'translateX(0)';
    }, 10);

    // Hide after 3 seconds
    setTimeout(() => {
      warning.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (warning.parentNode) {
          warning.parentNode.removeChild(warning);
        }
      }, 300);
    }, 3000);
  }




  /**
   * Calculate cart total excluding free gifts - SIMPLIFIED & RELIABLE
   */
  calculateCartTotalExcludingGifts(cartItems) {
    if (!cartItems || !Array.isArray(cartItems)) return 0;

    let total = 0;
    let excludedGifts = 0;

    if (this.debugMode) {
      console.log('🧮 === CART CALCULATION DEBUG ===');
      console.log('🧮 Total items to analyze:', cartItems.length);
    }

    for (const item of cartItems) {
      // Check if this item is a free gift
      const isGiftProduct = this.freeGiftProduct && item.product_id === parseInt(this.freeGiftProduct.id);
      const hasGiftProperties = item.properties &&
                               (item.properties._free_gift === 'true' ||
                                item.properties._minimum_order_gift === 'true');

      // SIMPLIFIED LOGIC: Only exclude items that have gift properties
      // This prevents circular dependency issues
      const shouldExcludeAsGift = isGiftProduct && hasGiftProperties;

      if (this.debugMode) {
        console.log(`🧮 Item analysis:`, {
          title: item.product_title || item.title,
          productId: item.product_id,
          variantId: item.variant_id,
          linePrice: this.formatMoney(item.line_price),
          originalLinePrice: this.formatMoney(item.original_line_price || item.line_price),
          quantity: item.quantity,
          isGiftProduct: isGiftProduct,
          hasGiftProperties: hasGiftProperties,
          properties: item.properties,
          shouldExcludeAsGift: shouldExcludeAsGift,
          action: shouldExcludeAsGift ? '❌ EXCLUDE' : '✅ INCLUDE'
        });
      }

      if (shouldExcludeAsGift) {
        excludedGifts++;
        if (this.debugMode) {
          console.log('🎁 Excluding gift item from total:', {
            productId: item.product_id,
            variantId: item.variant_id,
            linePrice: this.formatMoney(item.line_price),
            properties: item.properties
          });
        }
      } else {
        // CRITICAL FIX: Use original_line_price if available (before discounts)
        // This ensures that discount codes don't affect minimum order calculation
        const priceToUse = item.original_line_price || item.line_price;
        total += priceToUse;

        if (this.debugMode) {
          console.log('💰 Including item in total:', {
            productId: item.product_id,
            variantId: item.variant_id,
            linePrice: this.formatMoney(item.line_price),
            originalLinePrice: this.formatMoney(item.original_line_price || item.line_price),
            priceUsed: this.formatMoney(priceToUse),
            title: item.product_title,
            isGiftProduct: isGiftProduct,
            hasGiftProperties: hasGiftProperties,
            note: item.original_line_price ? 'Using original price (before discount)' : 'Using current price'
          });
        }
      }
    }

    if (this.debugMode) {
      console.log('🧮 Cart calculation summary:', {
        totalItems: cartItems.length,
        excludedGifts: excludedGifts,
        includedItems: cartItems.length - excludedGifts,
        calculatedTotal: this.formatMoney(total),
        note: 'Using original prices before discounts for minimum order calculation'
      });
      console.log('🧮 === END CART CALCULATION DEBUG ===');
    }

    return total;
  }

  /**
   * Load current cart data - SIMPLIFIED & OPTIMIZED
   */
  async loadCartData() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Store cart data first
      this.lastCartData = cart;
      this.cartTotal = cart.total_price;

      // CRITICAL FIX: Calculate cart total WITHOUT free gifts (not just discounts)
      // This ensures free gifts NEVER count towards minimum order value
      this.cartTotalWithoutDiscount = this.calculateCartTotalExcludingGifts(cart.items);

      // Validate calculation consistency
      if (this.debugMode && this.cartTotalWithoutDiscount > this.cartTotal) {
        console.warn('⚠️ Cart calculation inconsistency detected:', {
          cartTotal: this.formatMoney(this.cartTotal),
          calculatedTotal: this.formatMoney(this.cartTotalWithoutDiscount)
        });
      }

      if (this.debugMode) {
        console.log('🛒 Cart data updated:', {
          cartTotal: this.formatMoney(this.cartTotal),
          cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
          originalTotal: this.formatMoney(cart.original_total_price || cart.total_price),
          minimum: this.formatMoney(this.currentMinimum),
          minimumReached: this.cartTotalWithoutDiscount >= this.currentMinimum,
          itemCount: cart.item_count
        });
      }

      // Update gift status
      this.checkFreeGiftInCart(cart.items);
      this.applyGiftStyling();

      // Update UI
      this.updateProgressBar();
      this.updateCheckoutButton();

      // Force disable gift controls (only if cart has items)
      if (cart?.items?.length > 0) {
        setTimeout(() => {
          this.forceDisableGiftControls();
        }, 100);
      }
    } catch (error) {
      console.error('Failed to load cart data:', error);
    }
  }

  /**
   * Handle cart updates - IMPROVED to prevent race conditions
   */
  handleCartUpdate() {
    // CRITICAL FIX: Skip all processing if manual removal is in progress
    if (this.manualRemovalInProgress) {
      if (this.debugMode) {
        console.log('🛒 SKIPPING handleCartUpdate - manual removal in progress');
      }
      return;
    }

    // Prevent multiple simultaneous updates
    if (this.isProcessingUpdate) {
      if (this.debugMode) {
        console.log('🛒 Cart update already in progress, skipping...');
      }
      return;
    }

    // Optimized debounce with shorter delay for better UX
    clearTimeout(this.cartUpdateTimeout);
    this.cartUpdateTimeout = setTimeout(async () => {
      this.isProcessingUpdate = true;

      try {
        if (this.debugMode) {
          console.log('🛒 Processing cart update...');
        }

        await this.loadCartData();

        // Ensure cart drawer functionality
        this.ensureCartDrawerFunctionality();

        // CRITICAL: Always check cart state and force correction
        const minimumReached = this.cartTotalWithoutDiscount >= this.currentMinimum;

        // Check if discount code is applied
        const hasOurDiscount = this.lastCartData?.discount_codes?.some(code =>
          code.code === this.freeGiftDiscountCode
        ) || false;

        // ENHANCED CHECK: Only check for ACTUAL free gifts (with properties), not manual purchases
        const hasActualGiftInCart = this.lastCartData?.items?.some(item =>
          item.product_id === parseInt(this.freeGiftProduct?.id) &&
          item.properties && (item.properties._free_gift === 'true' || item.properties._minimum_order_gift === 'true')
        ) || false;

        if (this.debugMode) {
          console.log('🔍 CRITICAL CART CHECK:', {
            cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
            cartTotalRaw: this.formatMoney(this.cartTotal),
            minimum: this.formatMoney(this.currentMinimum),
            minimumReached: minimumReached,
            giftInCart: this.freeGiftInCart,
            giftApplied: this.giftApplied,
            hasOurDiscount: hasOurDiscount,
            hasActualGiftInCart: hasActualGiftInCart,
            discountCodes: this.lastCartData?.discount_codes
          });
        }

        // ENHANCED FORCE CORRECTION: Remove gift if minimum not reached (only actual gifts, not manual purchases)
        // CRITICAL FIX: Skip automatic gift removal if manual removal is in progress
        if (!minimumReached && (this.freeGiftInCart || this.giftApplied || hasOurDiscount || hasActualGiftInCart) && !this.manualRemovalInProgress) {
          if (this.debugMode) {
            console.log('🚨 FORCE CORRECTION: Minimum not reached but gift/discount exists!');
            console.log('🚨 Current state:', {
              cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
              minimum: this.formatMoney(this.currentMinimum),
              freeGiftInCart: this.freeGiftInCart,
              giftApplied: this.giftApplied,
              hasOurDiscount: hasOurDiscount,
              hasActualGiftInCart: hasActualGiftInCart,
              manualRemovalInProgress: this.manualRemovalInProgress,
              reasons: {
                freeGiftInCart: this.freeGiftInCart ? '✅ Gift detected in cart' : '❌',
                giftApplied: this.giftApplied ? '✅ Gift applied flag set' : '❌',
                hasOurDiscount: hasOurDiscount ? '✅ Discount code applied' : '❌',
                hasActualGiftInCart: hasActualGiftInCart ? '✅ Actual gift found in cart' : '❌'
              }
            });
            console.log('🚨 Will remove gift completely...');
          }

          // AGGRESSIVE REMOVAL: Use the new aggressive removal method
          await this.aggressiveGiftRemoval();
        } else if (!minimumReached && this.manualRemovalInProgress) {
          if (this.debugMode) {
            console.log('🛒 SKIPPING automatic gift removal - manual removal in progress');
          }

          // Force reload cart data after removal
          setTimeout(async () => {
            await this.loadCartData();
            this.updateProgressBar();
            if (this.debugMode) {
              console.log('🚨 Cart reloaded after gift removal');
            }
          }, 500);

        } else if (minimumReached && !this.freeGiftInCart && !this.giftApplied && !hasOurDiscount && !this.giftRemovalCooldown) {
          if (this.debugMode) {
            console.log('✅ APPLYING GIFT: Minimum reached and no gift exists');
          }
          await this.applyFreeGift();
        } else {
          if (this.debugMode) {
            console.log('✅ Cart state is correct, no action needed');
          }
        }

        // Update UI elements
        this.scheduleUIUpdate();

        // Ensure cart drawer stays functional (especially when empty)
        this.ensureCartDrawerFunctionality();

      } catch (error) {
        console.error('Error during cart update:', error);
      } finally {
        this.isProcessingUpdate = false;
      }

    }, 300); // Faster response time for better UX
  }

  /**
   * Schedule UI updates separately to prevent loops - OPTIMIZED
   */
  scheduleUIUpdate() {
    clearTimeout(this.uiUpdateTimeout);
    this.uiUpdateTimeout = setTimeout(() => {
      this.ensureProgressBarExists();
      this.updateProgressBar();
      this.updateCheckoutButton();

      // Force disable gift controls (only if cart has items)
      if (this.lastCartData?.items?.length > 0) {
        setTimeout(() => {
          this.forceDisableGiftControls();
        }, 50);
      }
    }, 100); // Faster UI updates for better UX
  }

  /**
   * Check and apply free gift if conditions are met - FIXED & ROBUST
   */
  async checkAndApplyFreeGift() {
    // Exit early if system is disabled or not configured
    if (!this.freeGiftEnabled || this.freeGiftMethod === 'disabled') {
      if (this.debugMode) {
        console.log('🎁 Free gift system disabled, skipping check');
      }
      return;
    }

    // Prevent multiple simultaneous operations
    if (this.isApplyingGift || this.isRemovingGift) {
      if (this.debugMode) {
        console.log('🎁 Gift operation already in progress, skipping...');
      }
      return;
    }

    // Ensure we have current cart data
    if (!this.lastCartData) {
      if (this.debugMode) {
        console.log('🎁 No cart data available, loading...');
      }
      await this.loadCartData();
    }

    // Use already calculated cart total (excluding gifts)
    const minimumReached = this.cartTotalWithoutDiscount >= this.currentMinimum;

    // Check discount status from already loaded cart data
    const hasOurDiscount = this.lastCartData?.discount_codes?.some(code =>
      code.code === this.freeGiftDiscountCode
    ) || false;

    if (this.debugMode) {
      console.log('🎁 Free gift check:', {
        cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
        cartTotalRaw: this.formatMoney(this.cartTotal),
        minimum: this.formatMoney(this.currentMinimum),
        minimumReached: minimumReached,
        giftInCart: this.freeGiftInCart,
        hasOurDiscount: hasOurDiscount,
        giftRemovalCooldown: this.giftRemovalCooldown
      });
    }

    // CRITICAL DECISION LOGIC
    if (minimumReached && !this.freeGiftInCart && !this.giftRemovalCooldown) {
      // Apply gift
      if (this.debugMode) {
        console.log('✅ Minimum reached, applying free gift...');
      }
      await this.applyFreeGift();
    } else if (!minimumReached && (this.freeGiftInCart || hasOurDiscount)) {
      // Remove gift completely - THIS IS THE CRITICAL FIX
      if (this.debugMode) {
        console.log('❌ Minimum NOT reached, removing free gift completely...', {
          cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
          minimum: this.formatMoney(this.currentMinimum),
          difference: this.formatMoney(this.currentMinimum - this.cartTotalWithoutDiscount)
        });
      }
      await this.forceCompleteGiftRemoval();
    } else {
      if (this.debugMode) {
        console.log('🎁 No action needed:', {
          minimumReached: minimumReached,
          giftInCart: this.freeGiftInCart,
          hasOurDiscount: hasOurDiscount,
          giftRemovalCooldown: this.giftRemovalCooldown
        });
      }
    }
  }

  /**
   * Remove free gift (product or discount) - IMPROVED
   */
  async removeFreeGift() {
    if (this.isRemovingGift) {
      if (this.debugMode) {
        console.log('🎁 Gift removal already in progress, skipping...');
      }
      return;
    }

    this.isRemovingGift = true;

    try {
      // For combined method, remove both product and discount
      const hasProduct = this.freeGiftProduct && this.freeGiftProduct.id;
      const hasDiscount = this.freeGiftDiscountCode;

      if (this.debugMode) {
        console.log('🎁 Removing free gift:', {
          hasProduct: hasProduct,
          hasDiscount: hasDiscount,
          method: this.freeGiftMethod,
          freeGiftInCart: this.freeGiftInCart,
          giftApplied: this.giftApplied
        });
      }

      if (hasProduct && hasDiscount) {
        // Combined method: Remove both to prevent abuse
        if (this.debugMode) {
          console.log('🎁 Removing combined gift: product + discount', {
            freeGiftInCart: this.freeGiftInCart,
            giftApplied: this.giftApplied
          });
        }

        // Remove discount first to prevent price confusion
        if (this.giftApplied) {
          await this.removeDiscountCode();
        }

        // Then remove product
        if (this.freeGiftInCart) {
          await this.removeFreeGiftFromCart();
        }

      } else if (this.freeGiftMethod === 'product') {
        await this.removeFreeGiftFromCart();
      } else if (this.freeGiftMethod === 'discount') {
        await this.removeDiscountCode();
      }

      // Reset all flags
      this.giftApplied = false;
      this.freeGiftInCart = false;

    } catch (error) {
      console.error('Error removing free gift:', error);
    } finally {
      this.isRemovingGift = false;
    }
  }

  /**
   * Force complete gift removal - IMMEDIATE & RELIABLE
   */
  async forceCompleteGiftRemoval() {
    if (this.isRemovingGift) {
      if (this.debugMode) {
        console.log('🎁 Gift removal already in progress, skipping...');
      }
      return;
    }

    this.isRemovingGift = true;

    try {
      if (this.debugMode) {
        console.log('🚨 FORCE COMPLETE GIFT REMOVAL - IMMEDIATE ACTION');
        console.log('🚨 Current state before removal:', {
          giftInCart: this.freeGiftInCart,
          giftApplied: this.giftApplied,
          cartTotal: this.formatMoney(this.cartTotal),
          cartTotalWithoutDiscount: this.formatMoney(this.cartTotalWithoutDiscount)
        });
      }

      // STEP 1: Reset internal states IMMEDIATELY
      this.giftApplied = false;
      this.freeGiftInCart = false;
      this.freeGiftQuantityInCart = 0;

      // STEP 2: Get current cart to check what needs to be removed
      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();

      if (this.debugMode) {
        console.log('🚨 Current cart before removal:', {
          items: currentCart.items.length,
          discountCodes: currentCart.discount_codes,
          totalPrice: this.formatMoney(currentCart.total_price)
        });
      }

      // STEP 3: Remove discount code FIRST (most critical)
      const hasOurDiscount = currentCart.discount_codes?.some(code =>
        code.code === this.freeGiftDiscountCode
      );

      if (hasOurDiscount && this.freeGiftDiscountCode) {
        if (this.debugMode) {
          console.log('🚨 Step 3: Removing discount code:', this.freeGiftDiscountCode);
        }
        await this.removeDiscountCode();
      }

      // STEP 4: Remove gift product from cart
      const hasGiftProduct = currentCart.items.some(item =>
        item.product_id === parseInt(this.freeGiftProduct?.id)
      );

      if (hasGiftProduct) {
        if (this.debugMode) {
          console.log('🚨 Step 4: Removing gift product from cart...');
        }
        await this.removeFreeGiftFromCart();
      }

      // STEP 5: IMMEDIATE cart refresh to show correct totals
      await this.loadCartData();

      // AGGRESSIVE UI UPDATE: Force immediate cart drawer refresh
      await this.forceCartDrawerRefresh();

      // Update progress bar and other UI elements
      this.updateProgressBar();

    // Update gift product UI controls
    this.updateGiftProductControls();

      if (this.debugMode) {
        console.log('✅ FORCE REMOVAL COMPLETE - Cart should now show correct totals');
        console.log('✅ Final state:', {
          giftInCart: this.freeGiftInCart,
          giftApplied: this.giftApplied,
          cartTotal: this.formatMoney(this.cartTotal),
          cartTotalWithoutDiscount: this.formatMoney(this.cartTotalWithoutDiscount)
        });
      }

    } catch (error) {
      console.error('Error during force gift removal:', error);
    } finally {
      this.isRemovingGift = false;
    }
  }

  /**
   * AGGRESSIVE gift removal - removes ALL instances of gift product immediately
   */
  async aggressiveGiftRemoval() {
    // Prevent multiple simultaneous removals
    if (this.isRemovingGift) {
      if (this.debugMode) {
        console.log('🚨 Gift removal already in progress, skipping aggressive removal');
      }
      return;
    }

    this.isRemovingGift = true;

    if (this.debugMode) {
      console.log('🚨 AGGRESSIVE GIFT REMOVAL - Removing ALL gift products immediately');
    }

    try {
      // STEP 1: Get current cart
      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();

      if (this.debugMode) {
        console.log('🚨 Current cart items before aggressive removal:', currentCart.items.length);
      }

      // STEP 2: Find gift products - SMART REMOVAL
      const giftProductsToRemove = [];
      const giftProductsToReduceQuantity = [];

      currentCart.items.forEach((item, index) => {
        const isGiftProduct = item.product_id === parseInt(this.freeGiftProduct?.id);

        if (isGiftProduct) {
          const hasGiftProperties = item.properties &&
                                   (item.properties._free_gift === 'true' ||
                                    item.properties._minimum_order_gift === 'true');

          if (hasGiftProperties) {
            // This is an automatically added gift - remove completely
            giftProductsToRemove.push({
              line: index + 1,
              productId: item.product_id,
              variantId: item.variant_id,
              quantity: item.quantity,
              title: item.title,
              properties: item.properties,
              reason: 'automatic_gift'
            });
          } else if (item.quantity > 1) {
            // This is manually added but has multiple quantities - reduce by 1
            giftProductsToReduceQuantity.push({
              line: index + 1,
              productId: item.product_id,
              variantId: item.variant_id,
              currentQuantity: item.quantity,
              newQuantity: item.quantity - 1,
              title: item.title,
              reason: 'reduce_manual_quantity'
            });
          }
          // If manually added with quantity 1, keep it (don't remove)
        }
      });

      if (this.debugMode) {
        console.log('🚨 Found gift products to remove completely:', giftProductsToRemove);
        console.log('🚨 Found gift products to reduce quantity:', giftProductsToReduceQuantity);
      }

      // STEP 3A: Remove automatic gifts completely
      for (const giftItem of giftProductsToRemove) {
        if (this.debugMode) {
          console.log('🚨 Removing automatic gift product:', giftItem.title, '(reason:', giftItem.reason + ')');
        }

        // Remove using line number
        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: giftItem.line,
            quantity: 0
          })
        });

        if (removeResponse.ok) {
          if (this.debugMode) {
            console.log('✅ Successfully removed automatic gift:', giftItem.title);
          }
        } else {
          // Try alternative method with variant ID
          const altResponse = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: giftItem.variantId,
              quantity: 0
            })
          });

          if (this.debugMode) {
            console.log(altResponse.ok ? '✅ Alternative removal successful' : '❌ Alternative removal failed');
          }
        }

        // Small delay between removals
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // STEP 3B: Reduce quantity for manual gifts (only if they have multiple quantities)
      for (const giftItem of giftProductsToReduceQuantity) {
        if (this.debugMode) {
          console.log('🚨 Reducing quantity for manual gift:', giftItem.title,
                     'from', giftItem.currentQuantity, 'to', giftItem.newQuantity);
        }

        const reduceResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: giftItem.line,
            quantity: giftItem.newQuantity
          })
        });

        if (reduceResponse.ok) {
          if (this.debugMode) {
            console.log('✅ Successfully reduced quantity for manual gift:', giftItem.title);
          }
        } else {
          if (this.debugMode) {
            console.log('❌ Failed to reduce quantity for manual gift:', giftItem.title);
          }
        }

        // Small delay between operations
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // STEP 4: Remove discount code if present
      if (this.freeGiftDiscountCode) {
        const hasDiscount = currentCart.discount_codes?.some(code =>
          code.code === this.freeGiftDiscountCode
        );

        if (hasDiscount) {
          if (this.debugMode) {
            console.log('🚨 Removing discount code:', this.freeGiftDiscountCode);
          }
          await this.removeDiscountCode();
        }
      }

      // STEP 5: Reset all internal states
      this.freeGiftInCart = false;
      this.giftApplied = false;
      this.freeGiftQuantityInCart = 0;

      // STEP 6: IMMEDIATE UI refresh
      if (this.debugMode) {
        console.log('🚨 Forcing immediate UI refresh...');
      }

      // Force immediate cart drawer refresh
      await this.forceCartDrawerRefresh();

      // Reload cart data
      await this.loadCartData();

      // Update UI elements
      this.updateProgressBar();
      this.updateGiftProductControls();

      // Force disable gift controls as backup (only if cart has items)
      if (this.lastCartData?.items?.length > 0) {
        setTimeout(() => {
          this.forceDisableGiftControls();
        }, 100);
      }

      if (this.debugMode) {
        console.log('✅ SMART GIFT REMOVAL COMPLETED');
        console.log('✅ Summary:', {
          automaticGiftsRemoved: giftProductsToRemove.length,
          manualGiftsQuantityReduced: giftProductsToReduceQuantity.length,
          discountRemoved: this.freeGiftDiscountCode ? 'Yes' : 'No'
        });
        console.log('✅ Final state:', {
          giftInCart: this.freeGiftInCart,
          giftApplied: this.giftApplied,
          cartTotal: this.formatMoney(this.cartTotal),
          cartTotalWithoutDiscount: this.formatMoney(this.cartTotalWithoutDiscount)
        });
      }

    } catch (error) {
      console.error('🚨 Error in aggressive gift removal:', error);
    } finally {
      this.isRemovingGift = false;
    }
  }

  /**
   * Update gift product controls - disable quantity buttons and remove buttons for automatic gifts
   */
  updateGiftProductControls() {
    if (!this.freeGiftProduct?.id) {
      if (this.debugMode) {
        console.log('🎁 No free gift product configured, skipping controls update');
      }
      return;
    }

    // SAFETY CHECK: Don't run if cart is empty
    if (!this.lastCartData?.items || this.lastCartData.items.length === 0) {
      if (this.debugMode) {
        console.log('🎁 Cart is empty, skipping gift controls update');
      }
      return;
    }

    try {
      // Find all cart items
      const cartItems = document.querySelectorAll('.cart-item-tile, .cart-item, tr[data-cart-item]');

      if (this.debugMode) {
        console.log('🎁 Found cart items for controls update:', cartItems.length);
        console.log('🎁 Looking for gift product:', {
          id: this.freeGiftProduct.id,
          handle: this.freeGiftProduct.handle,
          title: this.freeGiftProduct.title
        });
      }

      cartItems.forEach((cartItem, index) => {
        // Get all possible identifiers for this cart item
        const productLink = cartItem.querySelector('a[href*="/products/"]');
        const productHandle = productLink ? productLink.href.split('/products/')[1]?.split('?')[0] : null;

        // Try multiple ways to get product ID
        const productId = cartItem.dataset.productId ||
                         cartItem.querySelector('[data-product-id]')?.dataset.productId ||
                         cartItem.querySelector('[data-cart-item]')?.dataset.cartItem ||
                         cartItem.querySelector('input[name="updates[]"]')?.dataset.productId;

        // Try to get variant ID
        const variantId = cartItem.dataset.variantId ||
                         cartItem.querySelector('[data-variant-id]')?.dataset.variantId ||
                         cartItem.querySelector('input[name="updates[]"]')?.dataset.variantId;

        // Get title text
        const titleElement = cartItem.querySelector('.cart-items__title, .cart-item__title, h3, .product-title');
        const titleText = titleElement?.textContent?.trim();

        // Check if this is a gift product by multiple methods
        const isGiftByHandle = productHandle && this.freeGiftProduct.handle && productHandle === this.freeGiftProduct.handle;
        const isGiftById = productId && productId == this.freeGiftProduct.id;
        const isGiftByVariantId = variantId && variantId == this.freeGiftProduct.variantId;
        const isGiftByTitle = titleText && this.freeGiftProduct.title && titleText.includes(this.freeGiftProduct.title);

        // Additional check: Look for gift properties in the DOM
        const hasGiftProperties = this.checkIfItemHasGiftProperties(cartItem);
        const isGiftByProperties = hasGiftProperties;

        const isGiftProduct = isGiftByHandle || isGiftById || isGiftByVariantId || isGiftByTitle || isGiftByProperties;

        if (this.debugMode) {
          console.log(`🎁 Cart item ${index + 1}:`, {
            productHandle: productHandle,
            productId: productId,
            variantId: variantId,
            titleText: titleText,
            isGiftByHandle: isGiftByHandle,
            isGiftById: isGiftById,
            isGiftByVariantId: isGiftByVariantId,
            isGiftByTitle: isGiftByTitle,
            isGiftByProperties: isGiftByProperties,
            isGiftProduct: isGiftProduct,
            giftProductConfig: {
              id: this.freeGiftProduct.id,
              variantId: this.freeGiftProduct.variantId,
              handle: this.freeGiftProduct.handle,
              title: this.freeGiftProduct.title
            }
          });
        }

        if (isGiftProduct) {
          if (this.debugMode) {
            console.log('🎁 Found gift product in cart, applying controls...');
          }

          if (hasGiftProperties) {
            // This is an automatic gift - disable ALL controls
            this.disableGiftControls(cartItem, 'automatic');
          } else {
            // This is manually purchased - ENABLE all controls (treat as normal product)
            this.enableQuantityControlsForManualProduct(cartItem);
            if (this.debugMode) {
              console.log('✅ Manual purchase detected - keeping all controls enabled');
            }
          }
        }
      });

      if (this.debugMode) {
        console.log('🎁 Updated gift product controls');
      }

    } catch (error) {
      console.error('Error updating gift product controls:', error);
    }
  }

  /**
   * Check if cart item has gift properties
   */
  checkIfItemHasGiftProperties(cartItem) {
    // Look for hidden inputs or data attributes that indicate gift properties
    const giftIndicators = cartItem.querySelectorAll('input[name*="_free_gift"], input[name*="_minimum_order_gift"], [data-free-gift], [data-minimum-order-gift]');
    const hasDOMIndicator = giftIndicators.length > 0;

    // Check if this item was added by our system (check cart data)
    let hasCartDataIndicator = false;
    if (this.lastCartData?.items) {
      // Try to match this cart item with cart data
      const productId = cartItem.dataset.productId ||
                       cartItem.querySelector('[data-product-id]')?.dataset.productId ||
                       cartItem.querySelector('input[name="updates[]"]')?.dataset.productId;
      const variantId = cartItem.dataset.variantId ||
                       cartItem.querySelector('[data-variant-id]')?.dataset.variantId ||
                       cartItem.querySelector('input[name="updates[]"]')?.dataset.variantId;

      if (productId || variantId) {
        const matchingItem = this.lastCartData.items.find(item =>
          (productId && item.product_id == productId) ||
          (variantId && item.variant_id == variantId)
        );

        if (matchingItem && matchingItem.properties) {
          hasCartDataIndicator = matchingItem.properties._free_gift === 'true' ||
                                matchingItem.properties._minimum_order_gift === 'true';
        }
      }
    }

    return hasDOMIndicator || hasCartDataIndicator;
  }

  /**
   * Disable gift controls based on type
   */
  disableGiftControls(cartItem, type) {
    if (this.debugMode) {
      console.log('🎁 Disabling gift controls for type:', type);
    }

    // Find quantity selector with multiple selectors
    const quantitySelector = cartItem.querySelector('quantity-selector-component, .quantity-selector');
    const quantityButtons = cartItem.querySelectorAll('.quantity-minus, .quantity-plus, button[name="minus"], button[name="plus"]');
    const quantityInput = cartItem.querySelector('input[name="updates[]"], input[type="number"], input[data-cart-line]');
    const removeButton = cartItem.querySelector('.cart-items__remove, .cart__remove, [data-cart-remove], button[aria-label*="Remove"], button[aria-label*="remove"]');

    if (this.debugMode) {
      console.log('🎁 Found elements:', {
        quantitySelector: !!quantitySelector,
        quantityButtons: quantityButtons.length,
        quantityInput: !!quantityInput,
        removeButton: !!removeButton
      });
    }

    if (type === 'automatic') {
      // Automatic gift - disable everything and add visual indicators

      // Disable quantity buttons with multiple methods
      quantityButtons.forEach((button, index) => {
        if (this.debugMode) {
          console.log(`🎁 Disabling quantity button ${index + 1}:`, button);
        }

        button.disabled = true;
        button.style.opacity = '0.3';
        button.style.pointerEvents = 'none';
        button.style.cursor = 'not-allowed';
        button.title = 'Kostenloses Geschenk - Menge kann nicht geändert werden';
        button.setAttribute('data-gift-disabled', 'true');

        // Also add a class for CSS styling
        button.classList.add('gift-disabled');
      });

      // Disable quantity input and store original quantity
      if (quantityInput) {
        quantityInput.dataset.originalQuantity = quantityInput.value;
        quantityInput.disabled = true;
        quantityInput.readOnly = true;
        quantityInput.style.opacity = '0.3';
        quantityInput.style.cursor = 'not-allowed';
        quantityInput.title = 'Kostenloses Geschenk - Menge kann nicht geändert werden';
        quantityInput.setAttribute('data-gift-disabled', 'true');
        quantityInput.classList.add('gift-disabled');
      }

      // Hide remove button for automatic gifts
      if (removeButton) {
        removeButton.style.display = 'none';
        removeButton.setAttribute('data-gift-hidden', 'true');
      }

      // Add gift badge if not already present
      this.addGiftBadge(cartItem);

    } else if (type === 'manual') {
      // Manual gift - only disable quantity controls, keep remove button

      // Disable quantity buttons
      quantityButtons.forEach((button, index) => {
        if (this.debugMode) {
          console.log(`🎁 Disabling manual gift quantity button ${index + 1}:`, button);
        }

        button.disabled = true;
        button.style.opacity = '0.3';
        button.style.pointerEvents = 'none';
        button.style.cursor = 'not-allowed';
        button.title = 'Geschenkprodukt - Menge kann nicht geändert werden';
        button.setAttribute('data-gift-disabled', 'true');
        button.classList.add('gift-disabled');
      });

      // Disable quantity input and store original quantity
      if (quantityInput) {
        quantityInput.dataset.originalQuantity = quantityInput.value;
        quantityInput.disabled = true;
        quantityInput.readOnly = true;
        quantityInput.style.opacity = '0.3';
        quantityInput.style.cursor = 'not-allowed';
        quantityInput.title = 'Geschenkprodukt - Menge kann nicht geändert werden';
        quantityInput.setAttribute('data-gift-disabled', 'true');
        quantityInput.classList.add('gift-disabled');
      }

      // Keep remove button enabled for manual gifts
      if (removeButton) {
        removeButton.style.display = 'block';
        removeButton.disabled = false;
        removeButton.style.opacity = '1';
        removeButton.style.pointerEvents = 'auto';
      }
    }

    if (this.debugMode) {
      console.log('🎁 Gift controls disabled for type:', type);
    }
  }

  /**
   * Add gift badge to cart item
   */
  addGiftBadge(cartItem) {
    // Check if badge already exists
    if (cartItem.querySelector('.gift-badge, .kostenlos-badge')) return;

    // Find the image container
    const imageContainer = cartItem.querySelector('.cart-items__media, .cart-item__media');

    if (imageContainer) {
      const badge = document.createElement('div');
      badge.className = 'kostenlos-badge';
      badge.textContent = 'KOSTENLOS';
      badge.style.cssText = `
        position: absolute;
        top: 8px;
        right: 8px;
        background: #d90700;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: bold;
        z-index: 10;
        pointer-events: none;
      `;

      // Make sure image container is positioned relative
      imageContainer.style.position = 'relative';
      imageContainer.appendChild(badge);
    }
  }

  /**
   * Ensure progress bar exists (re-create if removed)
   */
  ensureProgressBarExists() {
    // Check if progress bar exists in DOM
    const existingProgress = document.querySelector('.minimum-order-progress');

    if (!existingProgress) {
      // Progress bar was removed, recreate it
      if (this.debugMode) {
        console.log('🛒 Progress bar was removed, re-creating...');
      }
      this.progressContainer = null;
      this.initProgressBar();
      return;
    }

    // Progress bar exists, but check if our reference is still valid
    if (!this.progressContainer || !document.contains(this.progressContainer)) {
      if (this.debugMode) {
        console.log('🛒 Progress bar reference lost, updating reference...');
      }
      this.progressContainer = existingProgress;
    }

    // Ensure progress bar is visible and properly styled
    if (this.progressContainer) {
      this.progressContainer.style.display = 'block';
      this.progressContainer.style.visibility = 'visible';

      // Force update to ensure content is current
      this.updateProgressBar();
    }
  }



  /**
   * Force remove discount with multiple methods (SAFE - NO REDIRECTS, NO CART CLEARING)
   */
  async forceRemoveDiscount() {
    if (this.debugMode) {
      console.log('🚨 SAFE FORCE REMOVING DISCOUNT CODE (NO REDIRECTS, NO CART CLEARING)');
    }

    // First, verify cart has items before attempting removal
    const cartCheck = await this.verifyCartIntegrity();
    if (!cartCheck.hasItems) {
      if (this.debugMode) {
        console.log('🚨 Cart is empty, skipping discount removal to prevent issues');
      }
      return;
    }

    // Try SAFE removal methods one by one (not simultaneously to avoid conflicts)
    try {
      if (this.debugMode) {
        console.log('🚨 Trying safe discount removal method 1...');
      }
      await this.removeDiscountCode();

      // Check if successful
      const check1 = await this.checkDiscountStatus();
      if (!check1.hasDiscount) {
        if (this.debugMode) {
          console.log('🚨 Discount removed successfully with method 1');
        }
        return;
      }

      if (this.debugMode) {
        console.log('🚨 Method 1 failed, trying method 2...');
      }
      await this.removeDiscountViaForm();

      // Check if successful
      const check2 = await this.checkDiscountStatus();
      if (!check2.hasDiscount) {
        if (this.debugMode) {
          console.log('🚨 Discount removed successfully with method 2');
        }
        return;
      }

    } catch (error) {
      console.error('🚨 Error in safe force discount removal:', error);
    }

    // Reset internal state
    this.giftApplied = false;

    // Show user notification if discount still exists
    setTimeout(() => {
      this.checkAndNotifyDiscountRemoval();
    }, 1000);
  }

  /**
   * Verify cart integrity before making changes
   */
  async verifyCartIntegrity() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return {
        hasItems: cart.items && cart.items.length > 0,
        itemCount: cart.items ? cart.items.length : 0,
        total: cart.total_price
      };
    } catch (error) {
      console.error('Error verifying cart integrity:', error);
      return { hasItems: false, itemCount: 0, total: 0 };
    }
  }

  /**
   * Check current discount status
   */
  async checkDiscountStatus() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const hasOurDiscount = cart.discount_codes &&
                            cart.discount_codes.some(code =>
                              code.code === this.freeGiftDiscountCode
                            );

      return {
        hasDiscount: hasOurDiscount,
        discountCodes: cart.discount_codes || []
      };
    } catch (error) {
      console.error('Error checking discount status:', error);
      return { hasDiscount: false, discountCodes: [] };
    }
  }



  /**
   * Remove discount via cart update (focused method)
   */
  async removeDiscountViaCartUpdate() {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount: '',
          note: 'discount_removed'
        })
      });

      if (this.debugMode) {
        console.log('🚨 Cart update discount removal:', response.ok ? 'SUCCESS' : 'FAILED');
      }
    } catch (error) {
      console.error('Cart update removal failed:', error);
    }
  }

  /**
   * Remove discount via cart clear (nuclear option)
   */
  async removeDiscountViaCartClear() {
    try {
      // Get current cart items first
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (cart.items.length === 0) {
        return; // Don't clear empty cart
      }

      // Clear cart (this removes discount)
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      // Re-add all items except our gift product
      for (const item of cart.items) {
        // Skip our gift product
        if (item.properties &&
            (item.properties._free_gift === 'true' ||
             item.properties._minimum_order_gift === 'true')) {
          continue;
        }

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {}
          })
        });
      }

      if (this.debugMode) {
        console.log('🚨 Nuclear cart clear and rebuild completed');
      }
    } catch (error) {
      console.error('Nuclear cart clear failed:', error);
    }
  }

  /**
   * Remove discount via alternative API (SAFE - NO CART CLEARING)
   */
  async removeDiscountViaAPI() {
    try {
      if (this.debugMode) {
        console.log('🚨 Attempting SAFE discount removal via alternative API');
      }

      // ONLY use safe endpoints that don't clear the cart
      const safeEndpoints = [
        '/cart/update.js'
      ];

      for (const endpoint of safeEndpoints) {
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              discount: '',
              note: '' // Clear discount but keep items
            })
          });

          if (response.ok) {
            if (this.debugMode) {
              console.log('🚨 SAFE alternative API discount removal successful via:', endpoint);
            }
            return;
          }
        } catch (error) {
          if (this.debugMode) {
            console.log('🚨 Safe endpoint failed:', endpoint, error);
          }
        }
      }
    } catch (error) {
      console.error('🚨 Safe alternative API discount removal failed:', error);
    }
  }

  /**
   * Remove discount via form submission (SAFE - NO REDIRECT)
   */
  async removeDiscountViaForm() {
    try {
      if (this.debugMode) {
        console.log('🚨 Attempting SAFE discount removal via form (AJAX only)');
      }

      // Use URLSearchParams instead of FormData for better control
      const params = new URLSearchParams();
      params.append('discount', ''); // Only clear discount, nothing else

      const response = await fetch('/cart/update', {
        method: 'POST',
        body: params,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (this.debugMode) {
          console.log('🚨 SAFE form-based discount removal successful');
          console.log('🚨 Items in cart after form update:', result.items?.length || 'unknown');
        }
      }
    } catch (error) {
      console.error('🚨 Safe form-based discount removal failed:', error);
    }
  }

  /**
   * Check and notify if discount removal was successful
   */
  async checkAndNotifyDiscountRemoval() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const hasOurDiscount = cart.discount_codes &&
                            cart.discount_codes.some(code =>
                              code.code === this.freeGiftDiscountCode
                            );

      if (hasOurDiscount) {
        // Discount still exists, show user notification
        this.showDiscountRemovalNotification();
        if (this.debugMode) {
          console.log('🚨 Discount still exists after removal attempts, showing user notification');
        }
      } else {
        if (this.debugMode) {
          console.log('🎁 Discount successfully removed');
        }
      }
    } catch (error) {
      console.error('Error checking discount removal:', error);
    }
  }

  /**
   * Check if free gift is already in cart
   */
  checkFreeGiftInCart(cartItems) {
    if (!this.freeGiftProduct) {
      this.freeGiftInCart = false;
      this.freeGiftQuantityInCart = 0;
      return;
    }

    const wasInCart = this.freeGiftInCart;

    // Find all items with our product ID
    const productItems = cartItems.filter(item =>
      item.product_id === parseInt(this.freeGiftProduct.id)
    );

    // Calculate total quantity and check for free gift properties
    let totalQuantity = 0;
    let freeGiftQuantity = 0;
    let manualQuantity = 0;

    productItems.forEach(item => {
      totalQuantity += item.quantity;

      // Check if this item has free gift properties (actual free gifts)
      if (item.properties &&
          (item.properties._free_gift === 'true' ||
           item.properties._minimum_order_gift === 'true')) {
        freeGiftQuantity += item.quantity;
      } else {
        // This is a manually added product (same product but purchased)
        manualQuantity += item.quantity;
      }
    });

    // ENHANCED LOGIC: Check if we have gift products and current minimum status
    const hasGiftProduct = productItems.length > 0;
    const hasActualGift = freeGiftQuantity > 0;
    const minimumCurrentlyReached = this.cartTotalWithoutDiscount >= this.currentMinimum;

    if (this.debugMode) {
      console.log('🎁 Gift check detailed:', {
        productId: this.freeGiftProduct.id,
        hasGiftProduct: hasGiftProduct,
        hasActualGift: hasActualGift,
        totalQuantity: totalQuantity,
        freeGiftQuantity: freeGiftQuantity,
        minimumReached: minimumCurrentlyReached,
        cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
        productItems: productItems.map(item => ({
          id: item.product_id,
          variant: item.variant_id,
          quantity: item.quantity,
          properties: item.properties
        }))
      });
    }

    // ENHANCED LOGIC: Only consider actual free gifts (with properties) as "freeGiftInCart"
    // Manual purchases of the same product should NOT be treated as free gifts
    this.freeGiftInCart = hasActualGift; // Only count actual free gifts
    this.freeGiftQuantityInCart = freeGiftQuantity;
    this.totalProductQuantityInCart = totalQuantity;
    this.manualProductQuantityInCart = manualQuantity;

    // If gift was in cart but now isn't, reset gift status AND remove discount
    if (wasInCart && !this.freeGiftInCart) {
      if (this.debugMode) {
        console.log('🎁 Free gift removed from cart, resetting status and removing discount');
      }
      this.giftApplied = false;



      // CRITICAL: Remove discount code when gift product is removed to prevent abuse
      if (this.freeGiftDiscountCode) {
        if (this.debugMode) {
          console.log('🚨 CRITICAL: Gift product removed, removing discount code to prevent abuse');
        }

        // Remove discount code (async without await to prevent syntax error)
        this.removeDiscountCode().catch(error => {
          console.error('Error removing discount code:', error);
        });
      }
    }

    if (this.debugMode) {
      console.log('🎁 Gift in cart check:', {
        productId: this.freeGiftProduct.id,
        cartItems: cartItems.length,
        totalProductQuantity: totalQuantity,
        freeGiftQuantity: freeGiftQuantity,
        freeGiftInCart: this.freeGiftInCart,
        wasInCart: wasInCart
      });
    }
  }

  /**
   * Initialize progress bar in cart drawer
   */
  initProgressBar() {
    // Don't create if already exists
    if (this.progressContainer && document.contains(this.progressContainer)) {
      console.log('🛒 Progress bar already exists, updating...');
      this.updateProgressBar();
      return;
    }

    // Find cart summary container with retry logic
    const findCartSummary = () => {
      return document.querySelector('.cart__summary-totals') ||
             document.querySelector('.cart-drawer__summary') ||
             document.querySelector('.cart__summary');
    };

    let cartSummary = findCartSummary();

    if (!cartSummary) {
      // Retry after a short delay (cart might still be loading)
      setTimeout(() => {
        cartSummary = findCartSummary();
        if (cartSummary) {
          this.createProgressBar(cartSummary);
        } else {
          console.warn('🛒 Cart summary container not found');
        }
      }, 100);
      return;
    }

    this.createProgressBar(cartSummary);
  }

  /**
   * Create the progress bar element
   */
  createProgressBar(cartSummary) {
    // Remove existing progress bar if any
    const existingProgress = cartSummary.querySelector('.minimum-order-progress');
    if (existingProgress) {
      existingProgress.remove();
    }

    // Create progress container
    this.progressContainer = document.createElement('div');
    this.progressContainer.className = 'minimum-order-progress';
    this.progressContainer.innerHTML = this.getProgressBarHTML();

    // Insert before cart total container
    const totalContainer = cartSummary.querySelector('.cart__total-container') ||
                          cartSummary.querySelector('.cart__checkout-button') ||
                          cartSummary.querySelector('[name="add"]');

    if (totalContainer) {
      cartSummary.insertBefore(this.progressContainer, totalContainer);
    } else {
      cartSummary.appendChild(this.progressContainer);
    }

    console.log('🛒 Progress bar created and inserted');
    this.updateProgressBar();
  }

  /**
   * Get progress bar HTML template
   */
  getProgressBarHTML() {
    return `
      <div class="minimum-order-progress__container">
        <div class="minimum-order-progress__info">
          <span class="minimum-order-progress__text"></span>
          <span class="minimum-order-progress__amount"></span>
        </div>
        <div class="minimum-order-progress__bar">
          <div class="minimum-order-progress__fill"></div>
        </div>
        <div class="minimum-order-progress__gift-info" style="display: none;">
          <span class="minimum-order-progress__gift-text"></span>
        </div>
      </div>
    `;
  }

  /**
   * Update progress bar display - FIXED
   */
  updateProgressBar() {
    if (!this.progressContainer || !this.currentMinimum) return;

    // Use corrected cart total that excludes free gifts
    // FALLBACK: If cartTotalWithoutDiscount is not set, calculate it now
    let totalForCalculation = this.cartTotalWithoutDiscount;
    if (totalForCalculation === undefined || totalForCalculation === null) {
      if (this.lastCartData?.items) {
        totalForCalculation = this.calculateCartTotalExcludingGifts(this.lastCartData.items);
        this.cartTotalWithoutDiscount = totalForCalculation;
      } else {
        totalForCalculation = this.cartTotal || 0;
      }
    }

    const remaining = Math.max(0, this.currentMinimum - totalForCalculation);
    const progress = Math.min(100, (totalForCalculation / this.currentMinimum) * 100);
    const isComplete = totalForCalculation >= this.currentMinimum;

    // DEBUG: Log progress bar calculation
    if (this.debugMode) {
      console.log('📊 Progress Bar Update:', {
        cartTotal: this.formatMoney(this.cartTotal),
        cartTotalWithoutDiscount: this.formatMoney(this.cartTotalWithoutDiscount),
        totalForCalculation: this.formatMoney(totalForCalculation),
        currentMinimum: this.formatMoney(this.currentMinimum),
        remaining: this.formatMoney(remaining),
        progress: `${progress.toFixed(1)}%`,
        isComplete: isComplete
      });
    }

    // Always show progress container
    this.progressContainer.style.display = 'block';

    // Update progress bar
    const fill = this.progressContainer.querySelector('.minimum-order-progress__fill');
    const text = this.progressContainer.querySelector('.minimum-order-progress__text');
    const amount = this.progressContainer.querySelector('.minimum-order-progress__amount');
    const giftInfo = this.progressContainer.querySelector('.minimum-order-progress__gift-info');
    const giftText = this.progressContainer.querySelector('.minimum-order-progress__gift-text');

    if (fill) fill.style.width = `${progress}%`;

    if (text && amount) {
      if (isComplete) {
        text.textContent = this.getTranslation('minimum_reached');
        amount.textContent = '';
        this.progressContainer.classList.add('minimum-order-progress--complete');
      } else {
        const remainingText = this.getTranslation('minimum_remaining').replace('{amount}', this.formatMoney(remaining));
        text.textContent = remainingText;
        amount.textContent = '';
        this.progressContainer.classList.remove('minimum-order-progress--complete');
      }
    }

    // Show gift info if enabled
    if (this.freeGiftEnabled && giftInfo && giftText) {
      giftInfo.style.display = 'block';

      if (isComplete && !this.giftApplied && !this.applyingGift) {
        giftText.textContent = this.getTranslation('free_gift_available');
        // Apply gift if not already applied
        if (!this.giftApplied && !this.isApplyingGift) {
          this.applyFreeGift();
        }
      } else if (!isComplete) {
        giftText.textContent = this.getTranslation('free_gift_when_minimum_reached');
      } else if (this.giftApplied) {
        giftText.textContent = this.getTranslation('free_gift_available');
      } else {
        giftInfo.style.display = 'none';
      }
    } else if (giftInfo) {
      giftInfo.style.display = 'none';
    }

    if (this.debugMode) {
      console.log('🛒 Progress updated:', {
        remaining: this.formatMoney(remaining),
        progress: `${progress}%`,
        isComplete,
        cartTotal: this.formatMoney(this.cartTotal),
        cartTotalWithoutDiscount: this.formatMoney(this.cartTotalWithoutDiscount),
        totalForCalculation: this.formatMoney(totalForCalculation),
        minimum: this.formatMoney(this.currentMinimum)
      });
    }
  }

  /**
   * Update checkout button state - FIXED
   */
  updateCheckoutButton() {
    const checkoutButtons = document.querySelectorAll('#checkout, .cart__checkout-button');

    // Use corrected cart total that excludes free gifts
    const isMinimumReached = this.cartTotalWithoutDiscount >= this.currentMinimum;

    if (this.debugMode) {
      console.log('🛒 Checkout button update:', {
        cartTotal: this.formatMoney(this.cartTotal),
        cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
        minimum: this.formatMoney(this.currentMinimum),
        isMinimumReached: isMinimumReached
      });
    }

    checkoutButtons.forEach(button => {
      if (isMinimumReached) {
        button.disabled = false;
        button.classList.remove('minimum-order-blocked');
        button.removeEventListener('click', this.blockCheckout);
      } else {
        button.disabled = true;
        button.classList.add('minimum-order-blocked');
        button.addEventListener('click', this.blockCheckout);
      }
    });
  }

  /**
   * Block checkout and show error message
   */
  blockCheckout = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // CRITICAL FIX: Use cartTotalWithoutDiscount for consistency with progress bar
    const remaining = this.currentMinimum - this.cartTotalWithoutDiscount;
    const message = this.getTranslation('checkout_blocked').replace('{amount}', this.formatMoney(remaining));

    // Show error message (you can customize this)
    alert(message);

    return false;
  }

  /**
   * Handle cart page redirect
   */
  handleCartPageRedirect() {
    // Check if we're on the cart page
    if (window.location.pathname === '/cart') {
      // Redirect to homepage and open drawer
      window.location.href = '/?open-cart=true';
    }

    // Check for URL parameter to open cart drawer
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('open-cart') === 'true') {
      setTimeout(() => {
        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }, 100);
    }
  }

  /**
   * Apply free gift (product or discount) - IMPROVED
   */
  async applyFreeGift() {
    if (!this.freeGiftEnabled || this.isApplyingGift) {
      return;
    }

    this.isApplyingGift = true;

    try {
      if (this.debugMode) {
        console.log('🎁 Applying free gift:', {
          method: this.freeGiftMethod,
          hasProduct: !!this.freeGiftProduct,
          hasDiscountCode: !!this.freeGiftDiscountCode
        });
      }

      // Check if we have both product and discount code configured
      const hasProduct = this.freeGiftProduct && this.freeGiftProduct.id;
      const hasDiscount = this.freeGiftDiscountCode;

      if (hasProduct && hasDiscount) {
        // Combined method: Add product + apply discount to make it free
        if (this.debugMode) {
          console.log('🎁 Using combined method: product + discount');
        }



        // First add the product
        await this.addFreeGiftToCart();

        // Then apply the discount code to make it free
        await this.applyDiscountCode();

      } else if (this.freeGiftMethod === 'product' && hasProduct) {
        // Product only method
        await this.addFreeGiftToCart();
      } else if (this.freeGiftMethod === 'discount' && hasDiscount) {
        // Discount only method
        await this.applyDiscountCode();
      } else {
        if (this.debugMode) {
          console.log('🎁 No valid gift method configured:', {
            method: this.freeGiftMethod,
            hasProduct: hasProduct,
            hasDiscount: hasDiscount
          });
        }
      }

      // Mark as applied
      this.giftApplied = true;

    } catch (error) {
      console.error('Error applying free gift:', error);
    } finally {
      this.isApplyingGift = false;
    }
  }

  /**
   * Check if discount code is already applied
   */
  async checkIfDiscountApplied() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Check if our discount code is in the applied discount codes
      const isApplied = cart.discount_codes &&
                       cart.discount_codes.some(code =>
                         code.code === this.freeGiftDiscountCode
                       );

      if (this.debugMode) {
        console.log('🎁 Discount check:', {
          discountCode: this.freeGiftDiscountCode,
          appliedCodes: cart.discount_codes,
          isApplied: isApplied
        });
      }

      return isApplied;
    } catch (error) {
      console.error('Failed to check discount status:', error);
      return false;
    }
  }

  /**
   * Apply discount code to cart using Shopify's cart update API
   */
  async applyDiscountCode() {
    if (!this.freeGiftDiscountCode) {
      if (this.debugMode) {
        console.log('🎁 No discount code configured');
      }
      return;
    }

    // Check if discount is already applied by checking actual cart
    const isAlreadyApplied = await this.checkIfDiscountApplied();
    if (isAlreadyApplied) {
      if (this.debugMode) {
        console.log('🎁 Discount already applied in cart');
      }
      this.giftApplied = true;
      return;
    }

    // Prevent multiple simultaneous attempts
    if (this.applyingGift) {
      if (this.debugMode) {
        console.log('🎁 Discount apply already in progress, skipping');
      }
      return;
    }

    this.applyingGift = true;

    try {
      if (this.debugMode) {
        console.log('🎁 Attempting to apply discount code:', this.freeGiftDiscountCode);
      }

      // Method 1: Use Shopify's cart update API (most reliable)
      const cartUpdateData = {
        discount: this.freeGiftDiscountCode,
        sections: ['cart-drawer', 'cart-items']
      };

      const response = await fetch(window.Shopify?.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartUpdateData)
      });

      if (response.ok) {
        const result = await response.json();
        this.giftApplied = true;

        if (this.debugMode) {
          console.log('🎁 Discount code applied successfully via cart update API:', result);
        }

        // Trigger cart update events
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: result }));

        // Update our cart data and UI gently
        setTimeout(async () => {
          await this.loadCartData();
          this.gentleCartRefresh();
          this.updateProgressBar();
          this.hideDuplicateDiscountCodes();
          // Apply gift styling after discount application
          setTimeout(() => this.applyGiftStyling(), 200);
        }, 300);

        return;
      } else {
        if (this.debugMode) {
          console.log('🎁 Cart update API failed, trying direct redirect method');
        }
      }

      // Method 2: Try form-based discount application (NO REDIRECT)
      if (this.debugMode) {
        console.log('🎁 Trying form-based discount application');
      }

      const formData = new FormData();
      formData.append('discount', this.freeGiftDiscountCode);

      const formResponse = await fetch('/cart', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (formResponse.ok) {
        this.giftApplied = true;
        if (this.debugMode) {
          console.log('🎁 Discount applied successfully via form method');
        }

        // Update cart data gently
        setTimeout(async () => {
          await this.loadCartData();
          this.gentleCartRefresh();
          this.updateProgressBar();
          this.hideDuplicateDiscountCodes();
        }, 500);

        return;
      }

      // Method 3: Show user notification to apply manually
      if (this.debugMode) {
        console.log('🎁 All automatic methods failed, showing user notification');
      }

      this.showDiscountApplicationNotification();

    } catch (error) {
      console.error('🎁 Error applying discount code:', error);

      // Show user notification instead of redirect
      if (this.debugMode) {
        console.log('🎁 Error occurred, showing user notification');
      }

      this.showDiscountApplicationNotification();
    } finally {
      this.applyingGift = false;
    }
  }

  /**
   * Remove discount code from cart - SIMPLIFIED VERSION
   */
  async removeDiscountCode() {
    if (!this.freeGiftDiscountCode) {
      return;
    }

    try {
      if (this.debugMode) {
        console.log('🎁 Attempting to remove discount code:', this.freeGiftDiscountCode);
      }

      // Simple and direct approach: Clear discount via cart update
      const updateData = {
        discount: '' // Clear discount
      };

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const result = await response.json();
        if (this.debugMode) {
          console.log('🎁 Discount removal successful:', result);
        }
        this.giftApplied = false;

        // Update cart display immediately
        this.refreshCartDrawer();
        this.updateProgressBar();

        return true;
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('🎁 Error removing discount code:', error);
      this.giftApplied = false;

      if (this.debugMode) {
        console.log('🎁 Discount removal failed, showing user notification');
      }
      this.showDiscountRemovalNotification();
      return false;
    }
  }

  /**
   * Show notification that discount needs manual removal
   */
  showDiscountRemovalNotification() {
    // Find progress bar to show message
    const progressBar = document.querySelector('.minimum-order-progress');
    if (progressBar) {
      const messageElement = progressBar.querySelector('.progress-message');
      if (messageElement) {
        const originalText = messageElement.textContent;
        messageElement.textContent = 'Bitte entfernen Sie den Gutschein-Code manuell aus dem Warenkorb';
        messageElement.style.color = '#d90700';
        messageElement.style.fontWeight = 'bold';

        // Reset after 5 seconds
        setTimeout(() => {
          messageElement.textContent = originalText;
          messageElement.style.color = '';
          messageElement.style.fontWeight = '';
        }, 5000);
      }
    }

    // Also try to show a browser notification
    if (this.debugMode) {
      console.log('🚨 MANUAL ACTION REQUIRED: Please remove discount code manually from cart');
    }

    // Try to highlight discount code input field
    const discountInput = document.querySelector('input[name="discount"]');
    if (discountInput) {
      discountInput.style.border = '2px solid #d90700';
      discountInput.style.backgroundColor = '#ffe6e6';

      setTimeout(() => {
        discountInput.style.border = '';
        discountInput.style.backgroundColor = '';
      }, 5000);
    }
  }

  /**
   * Show notification that discount needs manual application
   */
  showDiscountApplicationNotification() {
    // Find progress bar to show message
    const progressBar = document.querySelector('.minimum-order-progress');
    if (progressBar) {
      const messageElement = progressBar.querySelector('.progress-message');
      if (messageElement) {
        const originalText = messageElement.textContent;
        messageElement.textContent = `Bitte geben Sie den Gutschein-Code "${this.freeGiftDiscountCode}" manuell ein`;
        messageElement.style.color = '#7A8451';
        messageElement.style.fontWeight = 'bold';

        // Reset after 8 seconds
        setTimeout(() => {
          messageElement.textContent = originalText;
          messageElement.style.color = '';
          messageElement.style.fontWeight = '';
        }, 8000);
      }
    }

    // Try to highlight and pre-fill discount code input field
    const discountInput = document.querySelector('input[name="discount"]');
    if (discountInput) {
      discountInput.value = this.freeGiftDiscountCode;
      discountInput.style.border = '2px solid #7A8451';
      discountInput.style.backgroundColor = '#f0f8e6';
      discountInput.focus();

      setTimeout(() => {
        discountInput.style.border = '';
        discountInput.style.backgroundColor = '';
      }, 8000);
    }

    if (this.debugMode) {
      console.log('🎁 MANUAL ACTION REQUIRED: Please apply discount code manually:', this.freeGiftDiscountCode);
    }
  }

  /**
   * Show critical notification that discount MUST be removed manually
   */
  showCriticalDiscountRemovalNotification() {
    // Find progress bar to show CRITICAL message
    const progressBar = document.querySelector('.minimum-order-progress');
    if (progressBar) {
      const messageElement = progressBar.querySelector('.progress-message');
      if (messageElement) {
        messageElement.textContent = '🚨 KRITISCH: Entfernen Sie den Gutschein-Code SOFORT manuell! 🚨';
        messageElement.style.color = '#d90700';
        messageElement.style.fontWeight = 'bold';
        messageElement.style.fontSize = '16px';
        messageElement.style.textTransform = 'uppercase';
        messageElement.style.animation = 'blink 1s infinite';

        // Add blinking animation
        const style = document.createElement('style');
        style.textContent = `
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0.3; }
          }
        `;
        document.head.appendChild(style);
      }
    }

    // Try to highlight discount code input field with CRITICAL styling
    const discountInput = document.querySelector('input[name="discount"]');
    if (discountInput) {
      discountInput.style.border = '3px solid #d90700';
      discountInput.style.backgroundColor = '#ffcccc';
      discountInput.style.animation = 'blink 1s infinite';

      // Try to clear the input
      discountInput.value = '';
    }

    // Show browser alert as last resort
    setTimeout(() => {
      alert('🚨 KRITISCH: Der Gutschein-Code muss manuell entfernt werden! Bitte entfernen Sie den Code aus dem Gutschein-Feld.');
    }, 1000);

    if (this.debugMode) {
      console.log('🚨🚨🚨 CRITICAL: MANUAL DISCOUNT REMOVAL REQUIRED 🚨🚨🚨');
    }
  }

  /**
   * Add free gift to cart
   */
  async addFreeGiftToCart() {
    if (!this.freeGiftEnabled || !this.freeGiftProduct) {
      if (this.debugMode) {
        console.log('🎁 Skipping gift add: system disabled or no product configured');
      }
      return;
    }

    // STRONG protection against multiple adds
    if (this.addingGift) {
      if (this.debugMode) {
        console.log('🎁 Gift add already in progress, skipping');
      }
      return;
    }

    // Check current cart state before adding
    await this.loadCartData();

    // Check if FREE gift is already in cart (only check actual free gifts, not manually added products)
    if (this.freeGiftInCart && this.freeGiftQuantityInCart > 0) {
      if (this.debugMode) {
        console.log('🎁 Free gift already in cart, skipping add:', {
          freeGiftQuantity: this.freeGiftQuantityInCart,
          note: 'This only blocks automatic free gifts, manual purchases are still allowed'
        });
      }
      return;
    }

    // Set flag BEFORE starting the add process
    this.addingGift = true;

    try {
      if (this.debugMode) {
        console.log('🎁 Attempting to add free gift:', {
          product: this.freeGiftProduct
        });
      }

      // Use variant ID if specified, otherwise use product ID
      const variantId = this.freeGiftProduct.variantId || this.freeGiftProduct.id;

      if (this.debugMode) {
        console.log('🎁 Using variant ID:', variantId);
      }

      const addToCartData = {
        id: variantId,
        quantity: 1,
        properties: {
          '_free_gift': 'true',
          '_minimum_order_gift': 'true'
        },
        sections: this.getSectionIds()
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(addToCartData)
      });

      if (response.ok) {
        const result = await response.json();
        this.freeGiftInCart = true;

        if (this.debugMode) {
          console.log('🎁 Free gift added successfully:', result);
        }

        console.log('🎁 PRODUCT ADDED - Using gentle refresh...');

        // GENTLE APPROACH: Just refresh the cart content without events
        setTimeout(async () => {
          // Update cart sections if available
          if (result.sections) {
            this.updateCartSections(result.sections);
          }

          await this.loadCartData();
          this.updateProgressBar();
          this.applyGiftStyling();

          // Ensure cart drawer stays open
          const cartDrawer = document.querySelector('.cart-drawer__dialog');
          if (cartDrawer && !cartDrawer.open) {
            cartDrawer.showModal();
          }
        }, 200);

      } else {
        const errorText = await response.text();
        console.error('🎁 Failed to add free gift:', response.status, errorText);

        // Try to parse error response
        try {
          const errorData = JSON.parse(errorText);
          if (this.debugMode) {
            console.log('🎁 Error details:', errorData);
          }
        } catch (parseError) {
          // Error text is not JSON
        }

        // If product doesn't exist or is unavailable, disable gift feature
        if (response.status === 422) {
          console.warn('🎁 Free gift product unavailable, disabling gift feature');
          this.freeGiftEnabled = false;
        }
      }
    } catch (error) {
      console.error('🎁 Error adding free gift:', error);
    } finally {
      this.addingGift = false;
    }
  }

  /**
   * Check if minimum order value is currently reached - FIXED
   */
  isMinimumReached() {
    // Use the corrected calculation that excludes free gifts
    return this.cartTotalWithoutDiscount >= this.currentMinimum;
  }

  /**
   * Get section IDs for cart updates (like native add to cart)
   */
  getSectionIds() {
    const cartDrawer = document.querySelector('cart-items-component');
    const sectionId = cartDrawer?.dataset.sectionId;

    if (sectionId) {
      return [sectionId];
    }

    // Fallback section IDs
    return ['cart-drawer', 'cart-items'];
  }

  /**
   * Gentle cart refresh that doesn't break the drawer
   */
  gentleCartRefresh() {
    if (this.debugMode) {
      console.log('🔄 Gentle cart refresh...');
    }

    // Just trigger a simple content update
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      // Trigger a data refresh without affecting the drawer state
      const refreshEvent = new CustomEvent('cart:refresh', {
        bubbles: false,
        detail: { gentle: true, source: 'minimum-order-manager' }
      });

      cartItemsComponent.dispatchEvent(refreshEvent);

      if (this.debugMode) {
        console.log('🔄 Gentle refresh event dispatched');
      }
    }

    // Also update cart bubble/counter
    const cartBubble = document.querySelector('.cart-bubble') ||
                      document.querySelector('[data-cart-count]');
    if (cartBubble) {
      // Trigger cart count update
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          if (cartBubble.textContent !== undefined) {
            cartBubble.textContent = cart.item_count;
          }
        })
        .catch(error => {
          if (this.debugMode) {
            console.log('🔄 Cart count update failed:', error);
          }
        });
    }
  }

  /**
   * Update cart sections like native add to cart (without closing drawer)
   */
  updateCartSections(sections) {
    if (this.debugMode) {
      console.log('🔄 Updating cart sections:', Object.keys(sections));
    }

    // Update each section
    Object.entries(sections).forEach(([sectionId, sectionHTML]) => {
      // Find the cart items component
      const cartItemsComponent = document.querySelector(`[data-section-id="${sectionId}"]`) ||
                                document.querySelector('cart-items-component');

      if (cartItemsComponent) {
        // Parse the new HTML
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(sectionHTML, 'text/html');

        // Find the cart items container in the new HTML
        const newCartItems = newDoc.querySelector('.cart-items__wrapper') ||
                            newDoc.querySelector('.cart-items') ||
                            newDoc.querySelector('.cart-drawer__items');

        // Find the current cart items container
        const currentCartItems = cartItemsComponent.querySelector('.cart-items__wrapper') ||
                                cartItemsComponent.querySelector('.cart-items') ||
                                cartItemsComponent.querySelector('.cart-drawer__items');

        if (newCartItems && currentCartItems) {
          // Replace the content without affecting the drawer state
          currentCartItems.innerHTML = newCartItems.innerHTML;

          if (this.debugMode) {
            console.log('🔄 Updated cart items content for section:', sectionId);
          }
        }
      }
    });
  }

  /**
   * Remove free gift from cart - FIXED & ROBUST
   */
  async removeFreeGiftFromCart() {
    if (!this.freeGiftProduct) {
      if (this.debugMode) {
        console.log('🎁 Cannot remove gift - no gift product configured');
      }
      return;
    }

    try {
      if (this.debugMode) {
        console.log('🎁 Attempting to remove free gift from cart...');
      }

      // Get current cart to find the line item
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (this.debugMode) {
        console.log('🎁 Current cart items:', cart.items.map((item, index) => ({
          index: index + 1,
          productId: item.product_id,
          variantId: item.variant_id,
          title: item.title,
          quantity: item.quantity,
          properties: item.properties
        })));
      }

      // Find gift items with correct line numbers
      const giftItemsToRemove = [];

      cart.items.forEach((item, index) => {
        const isGiftProduct = item.product_id === parseInt(this.freeGiftProduct.id);
        const hasGiftProperties = item.properties &&
                                 (item.properties._free_gift === 'true' ||
                                  item.properties._minimum_order_gift === 'true');

        // FIXED LOGIC: Only remove items with gift properties (automatically added gifts)
        // NEVER remove manually added gift products, even if minimum is not reached
        const shouldRemove = isGiftProduct && hasGiftProperties;

        if (shouldRemove) {
          giftItemsToRemove.push({
            line: index + 1, // Shopify uses 1-based line numbers
            productId: item.product_id,
            variantId: item.variant_id,
            quantity: item.quantity,
            title: item.title,
            reason: 'automatic_gift_with_properties'
          });

          if (this.debugMode) {
            console.log('🚨 Marking AUTOMATIC gift item for removal:', {
              productId: item.product_id,
              variantId: item.variant_id,
              title: item.title,
              hasGiftProperties: hasGiftProperties,
              reason: 'automatic_gift_with_properties'
            });
          }
        } else if (isGiftProduct && !hasGiftProperties) {
          if (this.debugMode) {
            console.log('✅ PRESERVING manually added gift product:', {
              productId: item.product_id,
              variantId: item.variant_id,
              title: item.title,
              hasGiftProperties: hasGiftProperties,
              reason: 'manually_added_product'
            });
          }
        }
      });

      if (this.debugMode) {
        console.log('🎁 Found gift items to remove:', giftItemsToRemove);
      }

      if (giftItemsToRemove.length === 0) {
        if (this.debugMode) {
          console.log('🎁 No gift items found to remove');
        }
        return;
      }

      // Remove items one by one (more reliable than batch)
      for (const giftItem of giftItemsToRemove) {
        if (this.debugMode) {
          console.log('🎁 Removing gift item:', giftItem);
        }

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: giftItem.line,
            quantity: 0
          })
        });

        if (response.ok) {
          if (this.debugMode) {
            console.log('✅ Successfully removed gift item:', giftItem.title);
          }
        } else {
          const errorText = await response.text();
          console.error('❌ Failed to remove gift item:', {
            status: response.status,
            error: errorText,
            item: giftItem
          });

          // Try alternative removal method using variant ID
          if (giftItem.variantId) {
            if (this.debugMode) {
              console.log('🔄 Trying alternative removal method with variant ID...');
            }

            const altResponse = await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: giftItem.variantId,
                quantity: 0
              })
            });

            if (altResponse.ok) {
              if (this.debugMode) {
                console.log('✅ Successfully removed gift item using variant ID');
              }
            } else {
              console.error('❌ Alternative removal method also failed');
            }
          }
        }

        // Small delay between removals to prevent race conditions
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Update our state
      this.freeGiftInCart = false;
      this.freeGiftQuantityInCart = 0;

      // IMMEDIATE UI UPDATE: Force cart drawer refresh after removal
      if (this.debugMode) {
        console.log('🎁 Free gift removal completed - forcing UI update...');
      }

      // Force immediate cart drawer refresh
      await this.forceCartDrawerRefresh();

      if (this.debugMode) {
        console.log('🎁 Free gift removal and UI update completed');
      }

    } catch (error) {
      console.error('🎁 Failed to remove free gift:', error);
    }
  }

  /**
   * Get translation for given key in current language
   */
  getTranslation(key) {
    // Get texts for current language
    const currentTexts = this.allTexts[this.currentLanguage] || this.fallbackTexts[this.currentLanguage] || this.fallbackTexts.de;

    // Map internal keys to text keys
    const textMap = {
      'minimum_reached': currentTexts.reached,
      'minimum_remaining': currentTexts.remaining,
      'free_gift_available': currentTexts.giftAvailable,
      'free_gift_when_minimum_reached': currentTexts.giftWhenMinimum,
      'checkout_blocked': currentTexts.checkoutBlocked
    };

    const translation = textMap[key];

    if (this.debugMode && !translation) {
      console.warn('🌐 Translation missing for key:', key, 'language:', this.currentLanguage);
    }

    return translation || key;
  }

  /**
   * Debug function to manually test the system
   */
  debugTest() {
    console.log('🧪 === MINIMUM ORDER SYSTEM DEBUG TEST ===');
    console.log('🛒 System Status:', {
      enabled: this.enabled,
      debugMode: this.debugMode,
      country: this.currentCountry,
      minimum: this.formatMoney(this.currentMinimum),
      cartTotal: this.formatMoney(this.cartTotal),
      minimumReached: this.cartTotal >= this.currentMinimum
    });

    console.log('🎁 Free Gift Status:', {
      enabled: this.freeGiftEnabled,
      method: this.freeGiftMethod,
      product: this.freeGiftProduct,
      discountCode: this.freeGiftDiscountCode,
      inCart: this.freeGiftInCart,
      freeGiftQuantity: this.freeGiftQuantityInCart,
      totalProductQuantity: this.totalProductQuantityInCart,
      applied: this.giftApplied,
      addingGift: this.addingGift,
      hasBothProductAndDiscount: !!(this.freeGiftProduct && this.freeGiftDiscountCode),
      willUseCombinedMethod: !!(this.freeGiftProduct && this.freeGiftDiscountCode)
    });

    console.log('🌍 Country Detection:', {
      detected: this.currentCountry,
      fallback: this.fallbackCountry,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language
    });

    console.log('⚙️ Settings:', {
      minimumValues: this.minimumOrderValues,
      texts: this.allTexts[this.currentLanguage]
    });

    console.log('🔧 Available Test Functions:');
    console.log('- window.minimumOrderManager.testCountryDetection() - Test country detection');
    console.log('- window.minimumOrderManager.testFreeGift() - Test free gift application');
    console.log('- window.minimumOrderManager.testGiftRemoval() - Test gift removal');
    console.log('- window.minimumOrderManager.testGiftApplication() - Test gift application');
    console.log('- window.minimumOrderManager.forceCountry("AT"|"DE"|"EU") - Force specific country');
    console.log('- window.minimumOrderManager.resetGiftStatus() - Reset gift status');
  }

  /**
   * Test country detection manually
   */
  async testCountryDetection() {
    console.log('🧪 Testing country detection...');
    await this.detectCountry();
  }

  /**
   * Test gift removal manually - DEBUGGING FUNCTION
   */
  async testGiftRemoval() {
    console.log('🧪 Testing gift removal...');
    console.log('🧪 Current state:', {
      cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
      cartTotalRaw: this.formatMoney(this.cartTotal),
      minimum: this.formatMoney(this.currentMinimum),
      minimumReached: this.cartTotalWithoutDiscount >= this.currentMinimum,
      giftInCart: this.freeGiftInCart,
      giftEnabled: this.freeGiftEnabled
    });

    // Force gift removal
    await this.forceCompleteGiftRemoval();
  }

  /**
   * Test gift application manually - DEBUGGING FUNCTION
   */
  async testGiftApplication() {
    console.log('🧪 Testing gift application...');
    await this.checkAndApplyFreeGift();
  }

  /**
   * Test free gift application
   */
  async testFreeGift() {
    console.log('🧪 Testing free gift application...');
    if (this.freeGiftEnabled) {
      await this.applyFreeGift();
    } else {
      console.log('❌ Free gift system is disabled');
    }
  }

  /**
   * Force a specific country for testing
   */
  forceCountry(countryCode) {
    console.log('🧪 Forcing country to:', countryCode);
    this.setCountry(countryCode);
  }

  /**
   * Reset gift status for testing
   */
  resetGiftStatus() {
    console.log('🧪 Resetting gift status...');
    this.freeGiftInCart = false;
    this.giftApplied = false;
    this.addingGift = false;
    console.log('✅ Gift status reset');
  }

  /**
   * Force apply free gift for testing
   */
  async forceApplyGift() {
    console.log('🧪 Force applying free gift...');
    console.log('🎁 Current settings:', {
      freeGiftEnabled: this.freeGiftEnabled,
      freeGiftMethod: this.freeGiftMethod,
      freeGiftDiscountCode: this.freeGiftDiscountCode,
      freeGiftProduct: this.freeGiftProduct
    });

    if (this.freeGiftEnabled) {
      await this.applyFreeGift();
    } else {
      console.log('❌ Free gift system is disabled');
    }
  }

  /**
   * Force reset gift status and recheck
   */
  async forceResetAndRecheck() {
    console.log('🧪 Force resetting gift status...');
    this.giftApplied = false;
    this.freeGiftInCart = false;
    this.freeGiftQuantityInCart = 0;
    this.totalProductQuantityInCart = 0;
    this.addingGift = false;

    console.log('🧪 Rechecking cart status...');
    await this.handleCartUpdate();
  }

  /**
   * DEBUG: Force check and remove gift if minimum not reached
   */
  async debugForceGiftCheck() {
    console.log('🧪 DEBUG: Force checking gift status...');

    // Reload cart data first
    await this.loadCartData();

    const minimumReached = this.cartTotalWithoutDiscount >= this.currentMinimum;
    const hasOurDiscount = this.lastCartData?.discount_codes?.some(code =>
      code.code === this.freeGiftDiscountCode
    ) || false;

    console.log('🧪 Current state:', {
      cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
      minimum: this.formatMoney(this.currentMinimum),
      minimumReached: minimumReached,
      freeGiftInCart: this.freeGiftInCart,
      giftApplied: this.giftApplied,
      hasOurDiscount: hasOurDiscount,
      freeGiftQuantity: this.freeGiftQuantityInCart
    });

    // Force removal if minimum not reached but gift exists
    if (!minimumReached && (this.freeGiftInCart || this.giftApplied || hasOurDiscount)) {
      console.log('🚨 DEBUG: FORCING GIFT REMOVAL - minimum not reached but gift exists!');
      await this.forceCompleteGiftRemoval();
      console.log('✅ DEBUG: Gift removal completed');
    } else {
      console.log('✅ DEBUG: No action needed - state is correct');
    }
  }

  /**
   * Test immediate gift removal when minimum not reached
   */
  async testImmediateRemoval() {
    console.log('🧪 Testing immediate gift removal...');

    // Force load current cart data
    await this.loadCartData();

    console.log('🧪 Current state:', {
      cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
      cartTotalRaw: this.formatMoney(this.cartTotal),
      minimum: this.formatMoney(this.currentMinimum),
      minimumReached: this.cartTotalWithoutDiscount >= this.currentMinimum,
      giftInCart: this.freeGiftInCart,
      giftApplied: this.giftApplied
    });

    // Check if minimum not reached but gift exists
    const minimumReached = this.cartTotalWithoutDiscount >= this.currentMinimum;
    if (!minimumReached && (this.freeGiftInCart || this.giftApplied)) {
      console.log('🚨 SHOULD REMOVE: Minimum not reached but gift exists!');
      await this.aggressiveGiftRemoval();
      console.log('✅ Aggressive gift removal completed');
    } else {
      console.log('✅ No removal needed - state is correct');
    }
  }

  /**
   * Test aggressive gift removal manually
   */
  async testAggressiveRemoval() {
    console.log('🧪 Testing AGGRESSIVE gift removal...');
    await this.aggressiveGiftRemoval();
  }

  /**
   * Hide duplicate discount codes in cart drawer
   */
  hideDuplicateDiscountCodes() {
    if (!this.freeGiftDiscountCode) return;

    // Find all discount code displays in cart drawer
    const discountSelectors = [
      `[data-discount-code="${this.freeGiftDiscountCode}"]`,
      `.cart__discount-code:contains("${this.freeGiftDiscountCode}")`,
      `.cart__discount-item:contains("${this.freeGiftDiscountCode}")`,
      `.discount-code:contains("${this.freeGiftDiscountCode}")`
    ];

    discountSelectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          // Only hide if it's not our green progress banner
          if (!element.closest('.minimum-order-progress')) {
            element.style.display = 'none';
            if (this.debugMode) {
              console.log('🎁 Hidden duplicate discount code:', element);
            }
          }
        });
      } catch (error) {
        // Ignore selector errors
      }
    });

    // Also hide discount codes that appear below our progress bar
    const progressBar = document.querySelector('.minimum-order-progress');
    if (progressBar) {
      const nextElements = this.getNextSiblings(progressBar);
      nextElements.forEach(element => {
        if (element.textContent && element.textContent.includes(this.freeGiftDiscountCode)) {
          element.style.display = 'none';
          if (this.debugMode) {
            console.log('🎁 Hidden discount code below progress bar:', element);
          }
        }
      });
    }
  }

  /**
   * Get all next sibling elements
   */
  getNextSiblings(element) {
    const siblings = [];
    let nextElement = element.nextElementSibling;

    while (nextElement) {
      siblings.push(nextElement);
      nextElement = nextElement.nextElementSibling;
    }

    return siblings;
  }

  /**
   * Force cart drawer refresh with multiple aggressive methods
   */
  async forceCartDrawerRefresh() {
    try {
      if (this.debugMode) {
        console.log('🔄 FORCE refreshing cart drawer...');
      }

      // Method 1: Direct cart-items-component refresh (most effective)
      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent) {
        if (this.debugMode) {
          console.log('🔄 Found cart-items-component, triggering direct refresh...');
        }

        // Trigger the component's internal refresh mechanism
        cartItemsComponent.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { forceRefresh: true, source: 'minimum-order-manager' }
        }));

        // Also try to trigger the updateQuantity method with current state
        if (typeof cartItemsComponent.updateQuantity === 'function') {
          // This will trigger a full cart refresh
          cartItemsComponent.updateQuantity({ line: 1, quantity: 0, action: 'refresh' });
        }
      }

      // Method 2: Trigger immediate cart update events
      const immediateEvents = [
        new CustomEvent('cart:updated', { detail: { forceRefresh: true } }),
        new CustomEvent('cart:change', { detail: { forceRefresh: true } }),
        new CustomEvent('cart:refresh'),
        new CustomEvent('cart:item-added'),
        new CustomEvent('shopify:cart:update'),
        new CustomEvent('theme:cart:updated')
      ];

      immediateEvents.forEach(event => {
        document.dispatchEvent(event);
        if (this.debugMode) {
          console.log('🔄 Dispatched immediate event:', event.type);
        }
      });

      // Method 3: IMMEDIATE section refresh using Shopify's section API
      await this.immediateCartSectionRefresh();

      // Method 4: Try to reload cart drawer via fetch and DOM update
      await this.reloadCartDrawerContent();

      // Method 4: Try theme-specific refresh methods
      this.tryThemeSpecificRefresh();

      // Method 5: Force re-render of cart items if drawer is open
      this.forceCartItemsRerender();

      if (this.debugMode) {
        console.log('🔄 Force cart drawer refresh completed');
      }

    } catch (error) {
      console.error('🔄 Error in force cart drawer refresh:', error);
    }
  }

  /**
   * IMMEDIATE cart section refresh using Shopify's section API
   */
  async immediateCartSectionRefresh() {
    try {
      if (this.debugMode) {
        console.log('🔄 IMMEDIATE cart section refresh...');
      }

      // Get the cart-items-component section ID
      const cartItemsComponent = document.querySelector('cart-items-component');
      const sectionId = cartItemsComponent?.dataset?.sectionId;

      if (!sectionId) {
        if (this.debugMode) {
          console.log('🔄 No section ID found, skipping section refresh');
        }
        return;
      }

      // Fetch the updated section content
      const response = await fetch(`/cart?sections=${sectionId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const newSectionHTML = data[sectionId];

        if (newSectionHTML) {
          // Parse the new HTML
          const parser = new DOMParser();
          const newDoc = parser.parseFromString(newSectionHTML, 'text/html');
          const newCartItems = newDoc.querySelector('cart-items-component');

          if (newCartItems && cartItemsComponent) {
            // Replace the entire cart-items-component content
            cartItemsComponent.innerHTML = newCartItems.innerHTML;

            if (this.debugMode) {
              console.log('🔄 Cart items component content updated immediately');
            }

            // Trigger any necessary re-initialization
            cartItemsComponent.dispatchEvent(new CustomEvent('cart:section-updated', {
              detail: { sectionId: sectionId }
            }));
          }
        }
      }

    } catch (error) {
      if (this.debugMode) {
        console.log('🔄 Immediate cart section refresh failed:', error);
      }
    }
  }

  /**
   * Reload cart drawer content via fetch - IMPROVED
   */
  async reloadCartDrawerContent() {
    try {
      if (this.debugMode) {
        console.log('🔄 Reloading cart drawer content...');
      }

      // Force reload the entire cart drawer component
      const cartDrawerComponent = document.querySelector('cart-items-component') ||
                                 document.querySelector('.cart-items-component');

      if (cartDrawerComponent) {
        // Try to trigger a complete refresh of the cart items component
        if (typeof cartDrawerComponent.refresh === 'function') {
          cartDrawerComponent.refresh();
          if (this.debugMode) {
            console.log('🔄 Cart items component refreshed');
          }
        }

        // Force re-fetch cart data and re-render
        if (typeof cartDrawerComponent.loadCartData === 'function') {
          cartDrawerComponent.loadCartData();
          if (this.debugMode) {
            console.log('🔄 Cart data reloaded in component');
          }
        }
      }

      // Try to reload the entire cart drawer via section rendering
      try {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('sections', 'cart-drawer-items');

        const response = await fetch(currentUrl.toString(), {
          headers: {
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data['cart-drawer-items']) {
            const cartItemsContainer = document.querySelector('.cart-items__wrapper') ||
                                     document.querySelector('.cart-items');

            if (cartItemsContainer) {
              cartItemsContainer.innerHTML = data['cart-drawer-items'];
              if (this.debugMode) {
                console.log('🔄 Cart items updated via section rendering');
              }
            }
          }
        }
      } catch (error) {
        if (this.debugMode) {
          console.log('🔄 Section rendering failed:', error);
        }
      }

      // As a last resort, trigger a gentle cart drawer refresh without closing
      setTimeout(() => {
        const cartDrawer = document.querySelector('.cart-drawer__dialog');
        if (cartDrawer && cartDrawer.open) {
          // Instead of closing and reopening, just trigger a gentle refresh
          if (this.debugMode) {
            console.log('🔄 Gentle cart drawer refresh - keeping drawer open');
          }
          // Trigger cart update events to refresh content without closing
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      }, 1000);

    } catch (error) {
      if (this.debugMode) {
        console.log('🔄 Cart drawer content reload failed:', error);
      }
    }
  }

  /**
   * Try theme-specific refresh methods
   */
  tryThemeSpecificRefresh() {
    const refreshMethods = [
      () => window.theme?.cart?.refresh?.(),
      () => window.refreshCart?.(),
      () => window.updateCart?.(),
      () => window.cartDrawer?.refresh?.(),
      () => window.Cart?.refresh?.(),
      () => window.Shopify?.theme?.cart?.refresh?.(),
      () => window.DeSightApp?.refreshCart?.(),
      () => document.querySelector('cart-drawer')?.refresh?.(),
      () => document.querySelector('[data-cart-drawer]')?.refresh?.()
    ];

    refreshMethods.forEach((method, index) => {
      try {
        method();
        if (this.debugMode) {
          console.log(`🔄 Executed theme refresh method ${index + 1}`);
        }
      } catch (error) {
        // Ignore errors from missing methods
      }
    });
  }

  /**
   * Force re-render of cart items
   */
  forceCartItemsRerender() {
    // Find all cart-related elements and trigger updates
    const cartSelectors = [
      '.cart-drawer',
      '[data-cart-drawer]',
      '.cart-items',
      '[data-cart-items]',
      '.cart__items',
      'cart-drawer-component',
      'cart-items-component'
    ];

    cartSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        // Trigger various events that might cause re-render
        element.dispatchEvent(new CustomEvent('cart:updated'));
        element.dispatchEvent(new CustomEvent('refresh'));

        // Update data attributes to trigger observers
        element.setAttribute('data-last-updated', Date.now());

        if (this.debugMode) {
          console.log(`🔄 Triggered refresh on ${selector}`);
        }
      });
    });
  }

  /**
   * Refresh cart drawer content without page reload (LEGACY)
   */
  async refreshCartDrawer() {
    try {
      if (this.debugMode) {
        console.log('🔄 Refreshing cart drawer...');
      }

      // Method 1: Try to fetch cart sections using Shopify's section rendering
      const sectionIds = ['cart-drawer', 'cart-items', 'cart-notification'];
      const sectionUrl = `/cart?sections=${sectionIds.join(',')}`;

      const response = await fetch(sectionUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        // Update cart drawer content if available
        if (data['cart-drawer']) {
          const cartDrawer = document.querySelector('.cart-drawer');
          if (cartDrawer) {
            cartDrawer.innerHTML = data['cart-drawer'];
            if (this.debugMode) {
              console.log('🔄 Cart drawer updated via sections');
            }
          }
        }

        // Update cart items if available
        if (data['cart-items']) {
          const cartItems = document.querySelector('.cart-items');
          if (cartItems) {
            cartItems.innerHTML = data['cart-items'];
            if (this.debugMode) {
              console.log('🔄 Cart items updated via sections');
            }
          }
        }

        return;
      }

      // Method 2: Trigger multiple cart update events
      const events = [
        new CustomEvent('cart:updated', { detail: { refresh: true } }),
        new CustomEvent('cart:change', { detail: { refresh: true } }),
        new CustomEvent('cart:refresh'),
        new CustomEvent('shopify:cart:updated'),
        new CustomEvent('theme:cart:updated')
      ];

      events.forEach(event => {
        document.dispatchEvent(event);
        if (this.debugMode) {
          console.log('🔄 Dispatched event:', event.type);
        }
      });

      // Method 3: Try to trigger theme-specific cart refresh
      const refreshMethods = [
        () => window.theme?.cart?.refresh?.(),
        () => window.refreshCart?.(),
        () => window.updateCart?.(),
        () => window.cartDrawer?.refresh?.(),
        () => window.Cart?.refresh?.(),
        () => window.Shopify?.theme?.cart?.refresh?.()
      ];

      refreshMethods.forEach((method, index) => {
        try {
          method();
          if (this.debugMode) {
            console.log(`🔄 Executed refresh method ${index + 1}`);
          }
        } catch (error) {
          // Ignore errors from missing methods
        }
      });

      if (this.debugMode) {
        console.log('🔄 Cart drawer refresh attempted via events');
      }

    } catch (error) {
      console.error('🔄 Error refreshing cart drawer:', error);
    }
  }

  /**
   * Format money amount
   */
  formatMoney(cents) {
    const euros = cents / 100;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(euros);
  }

  /**
   * MANUAL DEBUG: Force check and remove gift if minimum not reached
   */
  async debugForceCheckAndRemove() {
    console.log('🧪 MANUAL DEBUG: Force checking cart state...');

    await this.loadCartData();

    const minimumReached = this.cartTotalWithoutDiscount >= this.currentMinimum;

    console.log('🧪 Current state:', {
      cartTotalExcludingGifts: this.formatMoney(this.cartTotalWithoutDiscount),
      minimum: this.formatMoney(this.currentMinimum),
      minimumReached: minimumReached,
      giftInCart: this.freeGiftInCart,
      giftApplied: this.giftApplied
    });

    if (!minimumReached && (this.freeGiftInCart || this.giftApplied)) {
      console.log('🚨 MANUAL: Minimum not reached but gift exists - FORCING REMOVAL');
      await this.forceCompleteGiftRemoval();
      console.log('✅ MANUAL: Gift removal completed');
    } else {
      console.log('✅ MANUAL: No action needed - state is correct');
    }
  }

  /**
   * DEBUG: Detailed cart calculation analysis
   */
  async debugCartCalculation() {
    console.log('🧪 DEBUGGING CART CALCULATION...');

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      console.log('🛒 Raw cart data:', {
        total_price: this.formatMoney(cart.total_price),
        original_total_price: this.formatMoney(cart.original_total_price || cart.total_price),
        item_count: cart.item_count,
        items_count: cart.items.length
      });

      console.log('🎁 Free gift configuration:', {
        freeGiftProduct: this.freeGiftProduct,
        freeGiftEnabled: this.freeGiftEnabled,
        freeGiftMethod: this.freeGiftMethod
      });

      console.log('📦 Cart items analysis:');
      cart.items.forEach((item, index) => {
        const isGiftProduct = this.freeGiftProduct && item.product_id === parseInt(this.freeGiftProduct.id);
        const hasGiftProperties = item.properties &&
                                 (item.properties._free_gift === 'true' ||
                                  item.properties._minimum_order_gift === 'true');
        const isActualGift = isGiftProduct && hasGiftProperties;

        console.log(`  Item ${index + 1}:`, {
          title: item.product_title,
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          line_price: this.formatMoney(item.line_price),
          price: this.formatMoney(item.price),
          isGiftProduct: isGiftProduct,
          hasGiftProperties: hasGiftProperties,
          isActualGift: isActualGift,
          properties: item.properties,
          includedInTotal: !isActualGift ? '✅ YES' : '❌ NO (excluded)'
        });
      });

      const calculatedTotal = this.calculateCartTotalExcludingGifts(cart.items);
      console.log('🧮 Calculation result:', {
        calculatedTotal: this.formatMoney(calculatedTotal),
        storedTotal: this.formatMoney(this.cartTotalWithoutDiscount),
        match: calculatedTotal === this.cartTotalWithoutDiscount ? '✅ MATCH' : '❌ MISMATCH'
      });

    } catch (error) {
      console.error('❌ Error in cart calculation debug:', error);
    }
  }

  /**
   * DEBUG: Test gift product controls
   */
  debugGiftProducts() {
    console.log('🎁 === DEBUGGING GIFT PRODUCT CONTROLS ===');

    if (!this.freeGiftProduct?.id) {
      console.log('❌ No free gift product configured');
      return;
    }

    console.log('🎁 Free gift product:', {
      id: this.freeGiftProduct.id,
      handle: this.freeGiftProduct.handle,
      title: this.freeGiftProduct.title,
      variantId: this.freeGiftProduct.variantId
    });

    // Find all cart items
    const cartItems = document.querySelectorAll('.cart-item-tile, .cart-item, tr[data-cart-item]');
    console.log('🛒 Found cart items:', cartItems.length);

    cartItems.forEach((cartItem, index) => {
      const productLink = cartItem.querySelector('a[href*="/products/"]');
      const productHandle = productLink ? productLink.href.split('/products/')[1]?.split('?')[0] : null;

      const productId = cartItem.dataset.productId ||
                       cartItem.querySelector('[data-product-id]')?.dataset.productId;

      const titleElement = cartItem.querySelector('.cart-items__title, .cart-item__title, h3');
      const titleText = titleElement?.textContent?.trim();

      const quantityButtons = cartItem.querySelectorAll('.quantity-minus, .quantity-plus, button[name="minus"], button[name="plus"]');
      const quantityInput = cartItem.querySelector('input[name="updates[]"], input[type="number"], input[data-cart-line]');
      const removeButton = cartItem.querySelector('.cart-items__remove, .cart__remove, [data-cart-remove], button[aria-label*="Remove"], button[aria-label*="remove"]');

      console.log(`🛒 Cart item ${index + 1}:`, {
        productHandle: productHandle,
        productId: productId,
        titleText: titleText,
        isGiftByHandle: productHandle === this.freeGiftProduct.handle,
        isGiftById: productId == this.freeGiftProduct.id,
        isGiftByTitle: titleText && titleText.includes(this.freeGiftProduct.title),
        quantityButtons: quantityButtons.length,
        quantityInput: !!quantityInput,
        removeButton: !!removeButton,
        hasGiftDisabledClass: cartItem.querySelector('.gift-disabled') ? true : false
      });
    });

    console.log('🎁 Use updateGiftProductControls() to apply controls');
  }

  /**
   * FORCE disable all quantity buttons for gift products - AGGRESSIVE APPROACH
   */
  forceDisableGiftControls() {
    if (this.debugMode) {
      console.log('🎁 === FORCE DISABLING ALL GIFT CONTROLS ===');
    }

    if (!this.freeGiftProduct?.id) {
      if (this.debugMode) {
        console.log('❌ No free gift product configured');
      }
      return;
    }

    // SAFETY CHECK: Don't run if cart is empty or no cart data
    if (!this.lastCartData?.items || this.lastCartData.items.length === 0) {
      if (this.debugMode) {
        console.log('✅ Cart is empty, no gift controls to disable');
      }
      return;
    }

    // Find gift products in cart data
    const giftItems = this.lastCartData.items.filter(item =>
      item.product_id == this.freeGiftProduct.id
    );

    if (this.debugMode) {
      console.log('🎁 Found gift items in cart data:', giftItems.length);
    }

    if (giftItems.length === 0) {
      if (this.debugMode) {
        console.log('✅ No gift products in cart, nothing to disable');
      }
      return;
    }

    // Find ALL quantity buttons and inputs in the cart and disable them if they belong to gift products
    const allQuantityButtons = document.querySelectorAll('button[name="minus"], button[name="plus"], .quantity-minus, .quantity-plus');
    const allQuantityInputs = document.querySelectorAll('input[name="updates[]"], input[type="number"][data-cart-line]');

    if (this.debugMode) {
      console.log('🔍 Found quantity controls:', {
        buttons: allQuantityButtons.length,
        inputs: allQuantityInputs.length
      });
    }

    // Disable all quantity buttons for gift products
    allQuantityButtons.forEach((button, index) => {
      // Check if this button belongs to a gift product
      const cartItem = button.closest('.cart-item-tile, .cart-item, tr[data-cart-item]');
      if (cartItem) {
        const isGiftProduct = this.isCartItemGiftProduct(cartItem);

        if (isGiftProduct) {
          if (this.debugMode) {
            console.log(`🎁 Disabling quantity button ${index + 1} for gift product`);
          }

          button.disabled = true;
          button.style.opacity = '0.3';
          button.style.pointerEvents = 'none';
          button.style.cursor = 'not-allowed';
          button.title = 'Geschenkprodukt - Menge kann nicht geändert werden';
          button.setAttribute('data-gift-disabled', 'true');
          button.classList.add('gift-disabled');
        }
      }
    });

    // Disable all quantity inputs for gift products
    allQuantityInputs.forEach((input, index) => {
      const cartItem = input.closest('.cart-item-tile, .cart-item, tr[data-cart-item]');
      if (cartItem) {
        const isGiftProduct = this.isCartItemGiftProduct(cartItem);

        if (isGiftProduct) {
          if (this.debugMode) {
            console.log(`🎁 Disabling quantity input ${index + 1} for gift product`);
          }

          input.disabled = true;
          input.readOnly = true;
          input.style.opacity = '0.3';
          input.style.cursor = 'not-allowed';
          input.title = 'Geschenkprodukt - Menge kann nicht geändert werden';
          input.setAttribute('data-gift-disabled', 'true');
          input.classList.add('gift-disabled');
        }
      }
    });

    if (this.debugMode) {
      console.log('✅ Force disable completed');
    }
  }

  /**
   * Check if a cart item is an ACTUAL gift product (only free gifts, not manual purchases)
   */
  isCartItemGiftProduct(cartItem) {
    if (!this.freeGiftProduct?.id || !this.lastCartData?.items) {
      return false;
    }

    // First check if this is the gift product by ID/handle
    let isGiftProductId = false;

    // Method 1: Check by product link
    const productLink = cartItem.querySelector('a[href*="/products/"]');
    if (productLink && this.freeGiftProduct.handle) {
      const productHandle = productLink.href.split('/products/')[1]?.split('?')[0];
      if (productHandle === this.freeGiftProduct.handle) {
        isGiftProductId = true;
      }
    }

    // Method 2: Check by title text matching cart data
    const titleElement = cartItem.querySelector('.cart-items__title, .cart-item__title, h3, .product-title');
    const titleText = titleElement?.textContent?.trim();

    if (titleText) {
      // Find matching item in cart data by title
      const matchingItem = this.lastCartData.items.find(item =>
        item.product_id == this.freeGiftProduct.id &&
        (item.product_title === titleText || item.title === titleText)
      );

      if (matchingItem) {
        isGiftProductId = true;
        // CRITICAL: Check if this item has gift properties (is actually a free gift)
        const hasGiftProperties = matchingItem.properties &&
          (matchingItem.properties._free_gift === 'true' || matchingItem.properties._minimum_order_gift === 'true');
        return hasGiftProperties; // Only return true for actual gifts
      }
    }

    // Method 3: Check by quantity input data attributes
    const quantityInput = cartItem.querySelector('input[name="updates[]"]');
    if (quantityInput) {
      const lineIndex = Array.from(document.querySelectorAll('input[name="updates[]"]')).indexOf(quantityInput);
      if (lineIndex >= 0 && this.lastCartData.items[lineIndex]) {
        const cartDataItem = this.lastCartData.items[lineIndex];
        if (cartDataItem.product_id == this.freeGiftProduct.id) {
          isGiftProductId = true;
          // CRITICAL: Check if this item has gift properties (is actually a free gift)
          const hasGiftProperties = cartDataItem.properties &&
            (cartDataItem.properties._free_gift === 'true' || cartDataItem.properties._minimum_order_gift === 'true');
          return hasGiftProperties; // Only return true for actual gifts
        }
      }
    }

    // If we found the gift product but no specific match, check DOM for gift properties
    if (isGiftProductId) {
      return this.checkIfItemHasGiftProperties(cartItem);
    }

    return false;
  }

  /**
   * Ensure cart drawer can open even when empty
   */
  ensureCartDrawerFunctionality() {
    // This function ensures the cart drawer doesn't get stuck when empty
    try {
      // Check if cart is empty
      if (!this.lastCartData?.items || this.lastCartData.items.length === 0) {
        if (this.debugMode) {
          console.log('🛒 Cart is empty, ensuring drawer functionality');
        }

        // Clear any processing flags that might block the drawer
        this.isProcessingUpdate = false;
        this.isApplyingGift = false;
        this.isRemovingGift = false;

        // Reset gift states for empty cart
        this.freeGiftInCart = false;
        this.giftApplied = false;
        this.freeGiftQuantityInCart = 0;

        // Ensure progress bar is hidden for empty cart
        if (this.progressContainer) {
          this.progressContainer.style.display = 'none';
        }

        // Ensure cart drawer component is properly initialized for empty state
        const cartDrawerComponent = document.querySelector('cart-drawer-component');
        if (cartDrawerComponent) {
          // Remove any stuck states that might prevent opening
          cartDrawerComponent.classList.remove('cart-drawer-disabled');

          // Ensure the dialog element is properly accessible
          const dialog = cartDrawerComponent.querySelector('dialog');
          if (dialog) {
            dialog.classList.remove('dialog-closing');
            // Clear any stuck animation states
            dialog.style.animation = '';
          }

          // Ensure cart drawer button is always clickable for empty cart
          const cartButton = cartDrawerComponent.querySelector('button[on\\:click*="open"]') ||
                            document.querySelector('[data-cart-drawer-toggle]') ||
                            document.querySelector('.header-actions__cart-icon');
          if (cartButton) {
            cartButton.style.pointerEvents = 'auto';
            cartButton.disabled = false;
            if (this.debugMode) {
              console.log('🛒 Cart drawer button ensured clickable for empty cart');
            }
          }
        }
      }
    } catch (error) {
      if (this.debugMode) {
        console.error('Error ensuring cart drawer functionality:', error);
      }
    }
  }

  /**
   * Destroy the module
   */
  destroy() {
    if (this.progressContainer) {
      this.progressContainer.remove();
    }

    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.periodicInterval) {
      clearInterval(this.periodicInterval);
    }

    clearTimeout(this.cartUpdateTimeout);

    // Remove event listeners
    document.removeEventListener('cart:updated', this.handleCartUpdate);
    document.removeEventListener('cart:change', this.handleCartUpdate);
  }
}

// Export for use in main.js - no automatic initialization
// The MinimumOrderManager is initialized in main.js to prevent double initialization

// Make debug functions globally available
window.debugForceCheckAndRemove = function() {
  if (window.minimumOrderManager) {
    window.minimumOrderManager.debugForceCheckAndRemove();
  } else {
    console.log('❌ MinimumOrderManager not found');
  }
};

window.debugCartCalculation = function() {
  if (window.minimumOrderManager) {
    window.minimumOrderManager.debugCartCalculation();
  } else {
    console.log('❌ MinimumOrderManager not found');
  }
};

window.testAggressiveRemoval = function() {
  if (window.minimumOrderManager) {
    window.minimumOrderManager.testAggressiveRemoval();
  } else {
    console.log('❌ MinimumOrderManager not found');
  }
};

window.debugGiftProducts = function() {
  if (window.minimumOrderManager) {
    window.minimumOrderManager.debugGiftProducts();
  } else {
    console.log('❌ MinimumOrderManager not found');
  }
};

window.updateGiftProductControls = function() {
  if (window.minimumOrderManager) {
    window.minimumOrderManager.updateGiftProductControls();
  } else {
    console.log('❌ MinimumOrderManager not found');
  }
};

window.forceDisableGiftControls = function() {
  if (window.minimumOrderManager) {
    window.minimumOrderManager.forceDisableGiftControls();
  } else {
    console.log('❌ MinimumOrderManager not found');
  }
};
